/* =========================================================================
   model.ts — financial model calculation engine
   Pure functions. No React. No DOM.
   ========================================================================= */

import {
  DEFAULT_CPI_ASSUMPTION,
  type Assumptions,
  type CheckResult,
  type Developer,
  type Flag,
  type KPIs,
  type ModelResult,
  type Scenario,
  type Year,
  type Benchmark,
  type SchoolSpecTier,
  type RentDecompositionComponent,
  type RentDecomposition,
  type RentMultiplierResult,
  type TpcSweepResult,
  type TpcSweepRow,
  type RentBuildUp,
  type RentBuildUpComponent,
  type LeaseVsOwnResult,
} from "./types";
import eduCosts from "./data/education_construction_costs_2021_2026.json";
import { JULIUS_BUILDER_QUOTES_SEED } from "./seeds/julius-builder-quotes";
import { generateActionItems } from "./action-items";
import {
  OPERATOR_AFFORDABILITY_CAUTION,
  OPERATOR_AFFORDABILITY_CEILING,
} from "./decision-thresholds";
import {
  applyVersatileRevGCostPreset,
  resolveCostAllowance,
} from "./developer-cost-basis";
import WATERLOO_WORKING_BASE_CASE from "./data/waterloo-working-base-case.json";

// ---------- constants ----------
import { countCashflowSignChanges, irr, npv } from "./solvers/irr";
export { irr, npv } from "./solvers/irr";
export { computeIrr, computeNpv } from "./solvers/irr";

import {
  breakEvenRent,
  breakEvenEnrolment,
  _setComputeCore,
} from "./solvers/break-even";
import { deriveGearedEquivalentHurdle } from "./solvers/geared-hurdle";
export {
  breakEvenRent,
  breakEvenEnrolment,
  EBITDA_EPS,
} from "./solvers/break-even";

import {
  monteCarlo as _monteCarlo,
  monteCarloChunked as _monteCarloChunked,
} from "./solvers/monte-carlo";

import {
  tornado,
  tornadoBars,
  DRIVERS,
  _setComputeCore as _tornadoSetComputeCore,
} from "./solvers/tornado";
import { ASSUMPTION_BOUNDS, assumptionBoundError } from "./assumption-bounds";
import { compactCurrency, preciseCurrency } from "./format";
export { tornado, tornadoBars, DRIVERS, pickKpi } from "./solvers/tornado";

// Wire computeCore into the break-even + tornado solvers (dependency injection for circular import).
// computeCore is a function declaration (hoisted), so this is safe at module level.
_setComputeCore(computeCoreRaw);
_tornadoSetComputeCore(computeCoreRaw);

const HURDLE_MARGIN = 0.005; // 0.5% — "marginal" band around developer hurdle
// Affordability / occupancy-cost guardrail on rent ÷ revenue. This is the
// AUTHORITATIVE model affordability band, sourced from decision-thresholds. It is
// DISTINCT from — and deliberately looser than — the
// aspirational ISA occupancy-cost TARGET of 10% in data/thresholds.json
// (isa_occupancy_cost_target). The two measure different things: this band is
// the deal-affordability ceiling; the ISA 10% is a stricter sector aspiration
// surfaced as an info badge. Do not reconcile them to a single number.
export const RENT_PCT_REVENUE_THRESHOLD = OPERATOR_AFFORDABILITY_CEILING;
export const RENT_PCT_REVENUE_CAUTION = OPERATOR_AFFORDABILITY_CAUTION;
export const ENROLMENT_RAMP_TOLERANCE = 0.9; // opening util within 90% of target = ok

// ---------- math helpers ----------
const sum = (arr: number[]): number =>
  Array.isArray(arr)
    ? arr.reduce((a, b) => a + (Number.isFinite(b) ? b : 0), 0)
    : 0;

const clone = <T>(o: T): T => structuredClone(o);

// IRR solver functions (npv, irr, irrNewton, irrSecant, irrBisection) are now
// imported from ./solvers/irr — see import at top of file.

// ---------- model utilities ----------
function rampUtil(
  year: number,
  capacity: number,
  rampYears: number,
  steady: number,
  start: number,
): number {
  if (capacity <= 0) return 0;
  if (year >= rampYears) return capacity * steady;
  const t = (year - 1) / Math.max(1, rampYears - 1);
  const eased = t * t * (3 - 2 * t);
  return capacity * (start + (steady - start) * eased);
}

function normalizeShares(shares: number[]): number[] {
  const cleaned = shares.map((s) => Math.max(0, Number.isFinite(s) ? s : 0));
  const total = sum(cleaned);
  return total > 0 ? cleaned.map((s) => s / total) : cleaned.map(() => 0);
}

function blendedTuitionFee(assumptions: Assumptions): number {
  const fees = [
    assumptions.earlyLearningFeeYear1,
    assumptions.primaryFeeYear1,
    assumptions.secondaryFeeYear1,
    assumptions.seniorFeeYear1,
  ];
  const shares = normalizeShares([
    assumptions.earlyLearningShare,
    assumptions.primaryShare,
    assumptions.secondaryShare,
    assumptions.seniorShare,
  ]);
  const blended = sum(fees.map((f, i) => Math.max(0, f) * shares[i]));
  return blended > 0 ? blended : assumptions.feePerStudentYear1;
}

function parkingIncome(assumptions: Assumptions): number {
  // D3 (#20): the flat per-bay casual income applies ONLY to bays not already allocated to a
  // priced use — event bays (weekday parent + weekend sports) earn their own event fees below, and
  // staff-reserved bays are not let casually. Charging the flat base on EVERY bay double-billed them.
  const flatBays = Math.max(
    0,
    assumptions.parkingBays -
      assumptions.weekdayParentParkingBays -
      assumptions.weekendSportsParkingBays -
      assumptions.staffReservedParkingBays,
  );
  const base = flatBays * assumptions.parkingIncomePerBay;
  const weekday =
    assumptions.weekdayParentParkingBays *
    assumptions.weekdayParentParkingFee *
    assumptions.weekdayParentParkingDays;
  const weekend =
    assumptions.weekendSportsParkingBays *
    assumptions.weekendSportsParkingFee *
    assumptions.weekendSportsParkingDays;
  return base + weekday + weekend;
}

// ---------- validation ----------
export function validate(
  scenario: Scenario,
): { path: string; message: string; level?: "error" | "warning" }[] {
  const errors: {
    path: string;
    message: string;
    level?: "error" | "warning";
  }[] = [];
  if (!scenario || typeof scenario !== "object") {
    errors.push({ path: "", message: "Scenario must be an object." });
    return errors;
  }
  const a = scenario.assumptions;
  const buildings = scenario.buildings;
  if (!a || typeof a !== "object") {
    errors.push({
      path: "assumptions",
      message: "Missing assumptions object.",
    });
    return errors;
  }
  const reqAssumptions = [
    "purchasePrice",
    "developmentMonths",
    "capRate",
    "discountRate",
    "modelYears",
    "leaseTermYears",
    "holdingInterestRate",
    "stampDutyRate",
    "acquisitionFeeRate",
    "agentsFeeRate",
    "consultantCostRate",
    "devManagementFeeRate",
    "developerContingencyRate",
    "developerHurdleIRR",
    "planningApprovalCosts",
    "otherDdCosts",
    "tenantRelocationCosts",
    "statutoryCostRate",
    "reddamFitoutAllowance",
    "fitoutAmortisationYears",
    "reddamCapexContribution",
    "rampYears",
    "steadyStateUtilisation",
    "startingUtilisation",
    "earlyLearningShare",
    "primaryShare",
    "secondaryShare",
    "seniorShare",
    "earlyLearningFeeYear1",
    "primaryFeeYear1",
    "secondaryFeeYear1",
    "seniorFeeYear1",
    "feePerStudentYear1",
    "tuitionGrowthPct",
    "rentIndexationPct",
    "fixedOpex",
    "fixedOpexGrowth",
    "variableCostPerStudent",
    "sportsFieldAreaSqm",
    "sportsFieldIncomePerSqm",
    "coveredPlayAreaGroundRent",
    "coveredPlayAreaConstructionCost",
    "parkingBays",
    "staffReservedParkingBays",
    "weekdayParentParkingBays",
    "weekdayParentParkingFee",
    "weekdayParentParkingDays",
    "weekendSportsParkingBays",
    "weekendSportsParkingFee",
    "weekendSportsParkingDays",
    "corporateTaxRate",
    "minimumCashReserveMonths",
    "foundingFamilyDiscount",
    "siblingDiscount",
    "discountDecayYears",
    "performanceBondPct",
    "defectsLiabilityMonths",
    "externalWorksPct",
    "bankGuaranteeMonths",
    "preliminariesRate",
    "builderMarginRate",
  ] as const;
  reqAssumptions.forEach((k) => {
    if (
      !(k in a) ||
      !Number.isFinite((a as unknown as Record<string, unknown>)[k] as number)
    ) {
      errors.push({
        path: `assumptions.${k}`,
        message: `Required numeric assumption '${k}' is missing or invalid.`,
      });
    }
  });
  if (!a.projectName || typeof a.projectName !== "string") {
    errors.push({
      path: "assumptions.projectName",
      message: "projectName is required.",
    });
  }
  if (!a.operatorName || typeof a.operatorName !== "string") {
    errors.push({
      path: "assumptions.operatorName",
      message: "operatorName is required.",
    });
  }
  // Every bounded numeric assumption is checked against the SAME table the assumption editor uses for its
  // inline error (src/assumption-bounds.ts), so nothing the UI would flag reaches compute() silently —
  // e.g. a −50% discount rate (NPV → +$291 trillion) or a 0.01% cap rate ($133B realisation) used to pass.
  for (const key of Object.keys(ASSUMPTION_BOUNDS)) {
    const value = (a as unknown as Record<string, unknown>)[key];
    // undefined is not "a value the editor would flag": required inputs are caught by the
    // reqAssumptions sweep above; the remaining bounded keys (schoolWacc, incentiveAmortisationYears,
    // developerExitYear, cpiAssumption, marketReviewPct, targetEnrolment, enrolmentCaptureRate,
    // approvalCostReddamSharePct) are documented runtime defaults in types.ts so scenarios saved
    // before they existed still load.
    if (value === undefined) continue;
    const message = assumptionBoundError(key, value);
    if (message)
      errors.push({ path: `assumptions.${key}`, message, level: "error" });
  }
  if (!Number.isInteger(a.rampYears) || a.rampYears < 1) {
    errors.push({
      path: "assumptions.rampYears",
      message: "rampYears must be a positive integer.",
      level: "error",
    });
  }
  if (
    a.rentEscalationMethod !== undefined &&
    !["fixed", "cpi", "market"].includes(a.rentEscalationMethod)
  ) {
    errors.push({
      path: "assumptions.rentEscalationMethod",
      message: "rentEscalationMethod must be fixed, cpi, or market.",
      level: "error",
    });
  }
  if (a.developerExitYear !== undefined) {
    // integer / 1–50 range is enforced by the bounds sweep above; report the horizon relation
    // only when the value itself is in range so each path carries one error.
    if (
      assumptionBoundError("developerExitYear", a.developerExitYear) ===
        undefined &&
      a.developerExitYear > a.modelYears
    ) {
      errors.push({
        path: "assumptions.developerExitYear",
        message: "developerExitYear cannot exceed modelYears.",
        level: "error",
      });
    }
    // Exit must be on/after the latest phased building completion. Mirror computeCore's
    // rounding/clamping rules so validation and the cashflow engine share one boundary.
    const fallbackDuration = Number.isFinite(a.developmentMonths)
      ? Math.max(1, Math.round(a.developmentMonths))
      : 1;
    const latestCompletionMonth = Array.isArray(buildings)
      ? buildings.reduce((latest, building) => {
          const startMonth = Math.max(
            0,
            Math.round(
              Number.isFinite(building.constructionStartMonth)
                ? building.constructionStartMonth!
                : 0,
            ),
          );
          const durationMonths = Math.max(
            1,
            Math.round(
              Number.isFinite(building.constructionMonths)
                ? building.constructionMonths!
                : fallbackDuration,
            ),
          );
          return Math.max(latest, startMonth + durationMonths);
        }, fallbackDuration)
      : fallbackDuration;
    if (a.developerExitYear * 12 < latestCompletionMonth) {
      errors.push({
        path: "assumptions.developerExitYear",
        message: `developerExitYear (${a.developerExitYear}) implies exit at month ${a.developerExitYear * 12}, before the latest phased completion month (${latestCompletionMonth}).`,
        level: "error",
      });
    }
  }
  // loop#001 logic review: in loaded mode the sheets price statutory cost from the consents
  // allowance (with a hard × statutory-rate fallback for invalid values). Require a usable
  // consents amount up front so the fallback can never silently change a board number.
  if (a.developerCostBasis?.mode === "loaded_aggregate") {
    const consents = a.developerCostAllowances?.consentsAmount;
    if (!Number.isFinite(consents) || (consents ?? 0) < 0) {
      errors.push({
        path: "assumptions.developerCostAllowances.consentsAmount",
        message:
          "Loaded cost basis requires a finite, non-negative developerCostAllowances.consentsAmount (the sheets price consents from it).",
        level: "error",
      });
    }
  }
  if (!Array.isArray(buildings)) {
    errors.push({
      path: "buildings",
      message: "Buildings must be an array.",
      level: "error",
    });
  } else if (buildings.length === 0) {
    errors.push({
      path: "buildings",
      message: "At least one building is required.",
      level: "error",
    });
  } else {
    buildings.forEach((b, i) => {
      // A7 (#74 review): in loaded-cost-basis mode compute() prices construction from the loaded
      // building works (prorated by GFA share), not from the per-sqm rate — so the per-sqm field
      // is not required there. Legacy mode still demands it: it is what compute() prices from.
      const loadedCostMode = a.developerCostBasis?.mode === "loaded_aggregate";
      const req = [
        "nlaSqm",
        "gfaSqm",
        ...(loadedCostMode ? [] : ["constructionPerSqmGfa"]),
        "netRentPerSqm",
        "outgoingsPerSqm",
        "comesOnlineYear",
        "enrolmentCapacity",
        "reddamCapex",
      ];
      req.forEach((k) => {
        if (
          !(k in b) ||
          !Number.isFinite(
            (b as unknown as Record<string, unknown>)[k] as number,
          )
        ) {
          errors.push({
            path: `buildings[${i}].${k}`,
            message: `Required numeric building field '${k}' is missing or invalid.`,
            level: "error",
          });
        }
      });
      if (b.nlaSqm < 0)
        errors.push({
          path: `buildings[${i}].nlaSqm`,
          message: "nlaSqm must be >= 0.",
          level: "error",
        });
      if (b.gfaSqm < b.nlaSqm)
        errors.push({
          path: `buildings[${i}].gfaSqm`,
          message: "gfaSqm must be >= nlaSqm.",
          level: "error",
        });
      if (b.constructionPerSqmGfa < 0)
        errors.push({
          path: `buildings[${i}].constructionPerSqmGfa`,
          message: "constructionPerSqmGfa must be >= 0.",
          level: "error",
        });
      if (b.netRentPerSqm < 0)
        errors.push({
          path: `buildings[${i}].netRentPerSqm`,
          message: "netRentPerSqm must be >= 0.",
          level: "error",
        });
      if (b.enrolmentCapacity < 0)
        errors.push({
          path: `buildings[${i}].enrolmentCapacity`,
          message: "enrolmentCapacity must be >= 0.",
          level: "error",
        });
      if (b.comesOnlineYear < 1)
        errors.push({
          path: `buildings[${i}].comesOnlineYear`,
          message: "comesOnlineYear must be >= 1.",
          level: "error",
        });
      if (
        Number.isInteger(a.modelYears) &&
        a.modelYears >= 1 &&
        b.comesOnlineYear > a.modelYears
      )
        errors.push({
          path: `buildings[${i}].comesOnlineYear`,
          message: `comesOnlineYear (${b.comesOnlineYear}) is after the model horizon (${a.modelYears} years): the building would carry its full construction cost and earn no revenue in the model.`,
          level: "error",
        });
      if (
        b.constructionStartMonth !== undefined &&
        !Number.isFinite(b.constructionStartMonth)
      )
        errors.push({
          path: `buildings[${i}].constructionStartMonth`,
          message: "constructionStartMonth must be a finite number when set.",
          level: "error",
        });
      if (
        b.constructionMonths !== undefined &&
        !Number.isFinite(b.constructionMonths)
      )
        errors.push({
          path: `buildings[${i}].constructionMonths`,
          message: "constructionMonths must be a finite number when set.",
          level: "error",
        });
      // A3 (#71 review + loop#001 logic review): phasing is months from T0 — compute() normalises to
      // completionMonth = max(0, round(start)) + max(1, round(duration)), where an UNSET duration
      // falls back to developmentMonths. The completion must land inside the modelled window:
      // completion at exactly modelYears·12 is the accepted boundary. When neither duration source
      // is available the rule cannot fire (nothing is known about completion timing).
      if (
        Number.isInteger(a.modelYears) &&
        a.modelYears >= 1 &&
        Number.isFinite(b.constructionStartMonth)
      ) {
        const durationMonths = Number.isFinite(b.constructionMonths)
          ? Math.max(1, Math.round(b.constructionMonths!))
          : Number.isFinite(a.developmentMonths) &&
              Number.isInteger(a.developmentMonths) &&
              a.developmentMonths >= 1
            ? Math.max(1, Math.round(a.developmentMonths))
            : null;
        if (durationMonths !== null) {
          const completionMonth =
            Math.max(0, Math.round(b.constructionStartMonth!)) + durationMonths;
          if (completionMonth > a.modelYears * 12)
            errors.push({
              path: `buildings[${i}].constructionStartMonth`,
              message: `Construction completes at month ${completionMonth} from T0, after the model horizon (${a.modelYears} years = ${a.modelYears * 12} months).`,
              level: "error",
            });
        }
      }
    });
    const totalCap = buildings.reduce(
      (sum, b) =>
        sum + (Number.isFinite(b.enrolmentCapacity) ? b.enrolmentCapacity : 0),
      0,
    );
    if (totalCap === 0) {
      errors.push({
        path: "buildings",
        message: "Total enrolment capacity is zero.",
        level: "warning",
      });
    }
  }
  return errors;
}

// ---------- main calc ----------
/** Single authoritative source for the escalation rate used in both the core
 *  cashflow loop and the external rent-decomposition / NPV helpers. */
/**
 * Lease year (from PC, 1-based) that contains the developer's exit month. `developerExitYear` counts
 * PROJECT years from T0; the landlord-NOI schedule and the operator P&L rows count LEASE years from PC.
 * Shared by compute() (exit valuation, hold-period legs) and the Excel Dev Cost exit formula so the two
 * can never drift. An exit at the PC month maps to lease year 1 (= sell-at-PC).
 */
export function exitLeaseYearFor(
  developerExitYear: number,
  lastCompletionMonth: number,
): number {
  return Math.max(
    1,
    Math.ceil((developerExitYear * 12 - lastCompletionMonth) / 12),
  );
}

export function effectiveRentGrowth(a: Assumptions): number {
  switch (a.rentEscalationMethod) {
    case "cpi":
      return Number.isFinite(a.cpiAssumption)
        ? (a.cpiAssumption as number)
        : DEFAULT_CPI_ASSUMPTION;
    case "market":
      return Number.isFinite(a.marketReviewPct)
        ? (a.marketReviewPct as number)
        : 0.04;
    case "fixed":
    default:
      return a.rentIndexationPct;
  }
}

// computeCore is the non-recursive engine core. It must NOT reference the
// break-even solvers (breakEvenRent / breakEvenEnrolment); those solvers call
// computeCore over scaled clones, so any call back into them here would recurse
// infinitely. The thin compute() wrapper below is the only public entry point.
function computeCoreRaw(scenario: Scenario): ModelResult {
  const a = scenario.assumptions;
  const rawBuildings = scenario.buildings;
  const buildings = Array.isArray(rawBuildings) ? rawBuildings : [];
  const flags: Flag[] = [];
  if (!Array.isArray(rawBuildings)) {
    flags.push({ level: "error", message: "Buildings must be an array." });
  }
  if (buildings.length === 0) {
    flags.push({
      level: "warning",
      message: "No buildings provided — computing developer costs only.",
    });
  }
  const years = new Array<Year>(a.modelYears);

  // ---- effective rent growth (S3.1) ----
  const rentGrowthRate = effectiveRentGrowth(a);

  // ---- developer exit year (S3.2) ----
  const developerExitYear = a.developerExitYear ?? a.modelYears;
  const useExitYear = developerExitYear < a.modelYears;

  // ---- physical aggregates (single-pass) ----
  // ---- statutory approvals + split (Phase G) ----
  const ssdaCost = a.ssdaCost ?? 0;
  const changeOfUseCost = a.changeOfUseCost ?? 0;
  const otherApprovalCosts = a.otherApprovalCosts ?? 0;
  const totalSplitApprovals = ssdaCost + changeOfUseCost + otherApprovalCosts;
  const reddamSharePct = Math.min(
    1,
    Math.max(0, a.approvalCostReddamSharePct ?? 0.5),
  );
  const reddamApprovalShare = totalSplitApprovals * reddamSharePct;
  const developerApprovalShare = totalSplitApprovals * (1 - reddamSharePct);

  const loadedCostBasis =
    a.developerCostBasis?.mode === "loaded_aggregate"
      ? a.developerCostBasis
      : undefined;
  const costBasisMode = loadedCostBasis
    ? ("loaded_aggregate" as const)
    : ("legacy_net_trade" as const);

  // The contribution is operator capex. For the developer it replaces debt
  // after gross capacity and equity are calculated; it does not reduce works.
  const reddamCapexContribution = Math.max(
    0,
    Number.isFinite(a.reddamCapexContribution) ? a.reddamCapexContribution : 0,
  );
  let totalNla = 0,
    totalGfa = 0,
    totalCap = 0,
    totalOpCapex =
      a.reddamFitoutAllowance + reddamApprovalShare + reddamCapexContribution;
  let hardConstruction = 0,
    netRentY1 = 0,
    outgoingsY1 = 0;
  for (const b of buildings) {
    totalNla += b.nlaSqm;
    totalGfa += b.gfaSqm;
    totalCap += b.enrolmentCapacity;
    totalOpCapex += b.reddamCapex;
    if (!loadedCostBasis) {
      hardConstruction += b.gfaSqm * b.constructionPerSqmGfa;
    }
    // P10-T5: Y1 net rent/outgoings should only include buildings that are actually online in Year 1.
    // Use the same bucketing rule as the yearly schedule (Math.max(1, Math.floor(...))) so fractional
    // comesOnlineYear values are treated consistently.
    const startYear = Math.max(1, Math.floor(b.comesOnlineYear));
    if (startYear <= 1) {
      netRentY1 += b.nlaSqm * b.netRentPerSqm;
      outgoingsY1 += b.nlaSqm * b.outgoingsPerSqm;
    }
  }

  if (totalNla === 0)
    flags.push({
      level: "error",
      message: "Campus has zero NLA. Add at least one building.",
    });
  if (totalCap === 0)
    flags.push({
      level: "warning",
      message: "Total enrolment capacity is zero.",
    });

  if (loadedCostBasis) {
    hardConstruction =
      Number.isFinite(loadedCostBasis.buildingWorks) &&
      loadedCostBasis.buildingWorks >= 0
        ? loadedCostBasis.buildingWorks
        : 0;
  }

  const legacySportsFieldConstruction =
    a.sportsFieldAreaSqm * a.sportsFieldConstructionPerSqm;
  const legacyParkingConstruction =
    a.parkingBays * a.parkingConstructionCostPerBay;
  const legacyCoveredPlayAreaConstruction = a.coveredPlayAreaConstructionCost;
  const legacyAmenityConstruction =
    legacySportsFieldConstruction +
    legacyParkingConstruction +
    legacyCoveredPlayAreaConstruction;
  const siteWorksConstruction = loadedCostBasis
    ? Math.max(
        0,
        Number.isFinite(loadedCostBasis.siteWorks)
          ? loadedCostBasis.siteWorks
          : 0,
      )
    : 0;
  const amenityConstruction = loadedCostBasis ? 0 : legacyAmenityConstruction;
  const loadedConstructionTotal =
    hardConstruction + siteWorksConstruction + amenityConstruction;

  // ---- developer cost stack ----
  // Builder additions: hardConstruction is the NET-TRADE rate (constructionPerSqmGfa); the builder's
  // preliminaries + margin sit on top to form the construction TPC. Modelled as % of hard, consistent
  // with the other %-of-hard lines (defaults from education_construction_costs_2021_2026.json).
  const preliminaries = loadedCostBasis
    ? 0
    : hardConstruction * a.preliminariesRate;
  const builderMargin = loadedCostBasis
    ? 0
    : hardConstruction * a.builderMarginRate;
  const allowanceBase = loadedCostBasis
    ? hardConstruction + siteWorksConstruction
    : hardConstruction;
  const consultantCosts = resolveCostAllowance(
    a.developerCostAllowances?.professionalFees,
    a.consultantCostRate,
    allowanceBase,
  ).amount;
  const devContingency = resolveCostAllowance(
    a.developerCostAllowances?.contingency,
    a.developerContingencyRate,
    allowanceBase,
  ).amount;
  const hasLoadedConsents =
    Boolean(loadedCostBasis) &&
    Number.isFinite(a.developerCostAllowances?.consentsAmount) &&
    (a.developerCostAllowances?.consentsAmount ?? 0) >= 0;
  const statutoryCosts = hasLoadedConsents
    ? a.developerCostAllowances!.consentsAmount!
    : hardConstruction * a.statutoryCostRate;
  const planningApprovalCosts = hasLoadedConsents ? 0 : a.planningApprovalCosts;
  const developerApprovalCosts = hasLoadedConsents ? 0 : developerApprovalShare;
  const stampDuty = a.purchasePrice * a.stampDutyRate;
  const acquisitionFee = a.purchasePrice * a.acquisitionFeeRate;

  const landAcq = a.purchasePrice + stampDuty + acquisitionFee;

  // ---- per-building construction phasing (E.1) ----
  const buildingData = buildings.map((b) => {
    const hard = loadedCostBasis
      ? hardConstruction *
        (totalGfa > 0
          ? b.gfaSqm / totalGfa
          : buildings.length > 0
            ? 1 / buildings.length
            : 0)
      : b.gfaSqm * b.constructionPerSqmGfa;
    const hardShare = hardConstruction > 0 ? hard / hardConstruction : 0;
    const consult = consultantCosts * hardShare;
    const statutory = statutoryCosts * hardShare;
    const contingency = devContingency * hardShare;
    const prelim = preliminaries * hardShare;
    const margin = builderMargin * hardShare;
    const startMonth = Math.max(
      0,
      Math.round(
        Number.isFinite(b.constructionStartMonth)
          ? b.constructionStartMonth!
          : 0,
      ),
    );
    const durationMonths = Math.max(
      1,
      Math.round(
        Number.isFinite(b.constructionMonths)
          ? b.constructionMonths!
          : a.developmentMonths,
      ),
    );
    const completionMonth = startMonth + durationMonths;
    return {
      hard,
      consult,
      statutory,
      contingency,
      prelim,
      margin,
      startMonth,
      durationMonths,
      completionMonth,
    };
  });
  const lastCompletionMonth =
    buildingData.length > 0
      ? Math.max(...buildingData.map((b) => b.completionMonth))
      : Math.max(1, Math.round(a.developmentMonths));
  const projectDevMonths = lastCompletionMonth / 12;

  // Site-wide costs are held from month 0 to last completion. In loaded mode,
  // the adopted site/outdoor pool replaces additive amenity construction.
  const siteWideCosts =
    landAcq +
    planningApprovalCosts +
    developerApprovalCosts +
    a.otherDdCosts +
    a.tenantRelocationCosts +
    siteWorksConstruction +
    amenityConstruction;

  // ---- construction debt rates (hoisted: the construction LVR sets the equity-funded share that bears
  //      the holding carry, so it must be known BEFORE holding interest is computed — P5-R1/D1) ----
  const constructionLvr = Number.isFinite(a.constructionLvrRate)
    ? Math.min(1, Math.max(0, a.constructionLvrRate))
    : 0;
  const termLvr = Number.isFinite(a.termLoanLvrRate)
    ? Math.min(1, Math.max(0, a.termLoanLvrRate))
    : 0;
  const devMargin = Number.isFinite(a.constructionMarginRate)
    ? a.constructionMarginRate
    : 0;
  const lineFeeRate = Number.isFinite(a.constructionLineFeeRate)
    ? a.constructionLineFeeRate
    : 0;
  const termMargin = Number.isFinite(a.termLoanMarginRate)
    ? a.termLoanMarginRate
    : 0;
  const holdYears = Number.isFinite(a.termLoanHoldYears)
    ? Math.max(0, Math.round(a.termLoanHoldYears))
    : 0;
  const baseRate = Number.isFinite(a.holdingInterestRate)
    ? a.holdingInterestRate
    : 0;

  // ---- P5-R1 (roots #1/#5): holding interest on the EQUITY-funded share only ----
  // The construction facility funds `constructionLvr` of the (ex-holding) cost base; equity funds the
  // rest. The holding carry is borne by the equity slice ONLY — the debt-funded slice's carry lives in
  // `debtCostDev` (construction interest @ base+margin + line fee), so it is no longer double-charged
  // (previously holding hit the FULL base at the holding rate AND the debt slice was charged again).
  // The circularity (facility sized on a base that included holding) is resolved per CONTEXT D1 by
  // sizing the facility on the EX-HOLDING base (below); the equity fraction is then a pure function of
  // the LVR input — no fixed point needed. Effect: holding FALLS as LVR rises; geared IRR responds.
  const equityFraction = 1 - constructionLvr;
  const siteWideHolding =
    siteWideCosts * equityFraction * a.holdingInterestRate * projectDevMonths;
  // Building-specific costs: each held for its own duration, average balance = 0.5 × cost (linear spend)
  const buildingHolding = buildingData.reduce((sum, b) => {
    const bldDd =
      b.hard + b.consult + b.statutory + b.contingency + b.prelim + b.margin;
    return (
      sum +
      0.5 *
        bldDd *
        equityFraction *
        a.holdingInterestRate *
        (b.durationMonths / 12)
    );
  }, 0);

  const holdingInterest = siteWideHolding + buildingHolding;

  // Full holding interest (equityFraction=1) for the ungeared (project) IRR, which must be
  // independent of financing. The equity-scaled holdingInterest is used in geared paths only.
  const siteWideFullHolding =
    siteWideCosts * a.holdingInterestRate * projectDevMonths;
  const buildingFullHolding = buildingData.reduce((sum, b) => {
    const bldDd =
      b.hard + b.consult + b.statutory + b.contingency + b.prelim + b.margin;
    return sum + 0.5 * bldDd * a.holdingInterestRate * (b.durationMonths / 12);
  }, 0);
  const fullHoldingInterest = siteWideFullHolding + buildingFullHolding;

  // All tenant/fitout costs below are assumption-sourced — no hardcoded figures.
  // preMgmt = the developer's REAL cost stack (= TDC minus the DM fee). UNCHANGED: land and the
  // (equity-share) holding interest are genuine costs and always sit in TDC, regardless of the DM-fee base.
  const preMgmt =
    landAcq +
    hardConstruction +
    preliminaries +
    builderMargin +
    consultantCosts +
    statutoryCosts +
    devContingency +
    planningApprovalCosts +
    developerApprovalCosts +
    holdingInterest +
    a.otherDdCosts +
    a.tenantRelocationCosts +
    siteWorksConstruction +
    amenityConstruction;
  // Ex-holding cost base — the slice the facility funds (construction + the non-finance DM fee), used to
  // size the facility without the holding↔facility circularity (CONTEXT D1).
  const preMgmtExHolding = preMgmt - holdingInterest;

  // ---- development management fee BASE (configurable inclusions) ----
  // The DM fee is always charged on development/construction costs. Land + all finance are EXCLUDED
  // by default and re-includable per toggle. Land + holding interest are known here; line fee +
  // construction interest depend on the facility (sized just below), so they fold in afterwards.
  const dmFeeDevCosts =
    hardConstruction +
    preliminaries +
    builderMargin +
    consultantCosts +
    statutoryCosts +
    devContingency +
    planningApprovalCosts +
    developerApprovalCosts +
    a.otherDdCosts +
    a.tenantRelocationCosts +
    siteWorksConstruction +
    amenityConstruction;
  const dmFeeBaseExFinance =
    dmFeeDevCosts +
    (a.dmFeeIncludeLand ? landAcq : 0) +
    (a.dmFeeIncludeHoldingInterest ? holdingInterest : 0);

  // ---- construction facility sizing (EX-HOLDING base — CONTEXT D1) ----
  // The facility funds construction + the non-finance DM fee, NOT the equity-funded holding carry. Its
  // DM-fee component therefore EXCLUDES holding from the base (and finance, folded post-sizing). Sizing
  // on this base breaks the holding↔facility circularity deterministically.
  const dmFeeBaseExHolding = dmFeeDevCosts + (a.dmFeeIncludeLand ? landAcq : 0);
  const devFeeInput = a.developerCostAllowances?.developmentFee;
  const devFeeRate =
    devFeeInput?.mode === "rate" && Number.isFinite(devFeeInput.rate)
      ? Math.max(0, devFeeInput.rate)
      : Math.max(0, a.devManagementFeeRate);
  const devMgmtFeeExHolding =
    devFeeInput?.mode === "amount" && Number.isFinite(devFeeInput.amount)
      ? Math.max(0, devFeeInput.amount)
      : dmFeeBaseExHolding * devFeeRate;
  const totalCostForFacility = preMgmtExHolding + devMgmtFeeExHolding;

  const grossFacilityLimitDev = totalCostForFacility * constructionLvr;
  const contributionExceedsDebt =
    reddamCapexContribution > grossFacilityLimitDev + 0.01;
  if (contributionExceedsDebt) {
    flags.push({
      level: "error",
      message: `Operator contribution ${compactCurrency(reddamCapexContribution)} exceeds gross debt capacity ${compactCurrency(grossFacilityLimitDev)}; excess is not applied to developer equity.`,
    });
  }
  const operatorContributionApplied = Math.min(
    reddamCapexContribution,
    grossFacilityLimitDev,
  );
  const facilityLimitDev = Math.max(
    0,
    grossFacilityLimitDev - operatorContributionApplied,
  );
  const contributionTiming = a.contributionTiming ?? "progressive";
  const lineFeeBase =
    contributionTiming === "completion"
      ? grossFacilityLimitDev
      : facilityLimitDev;
  const lineFee = lineFeeBase * lineFeeRate;

  // Monthly target balances distinguish use-first, progressive and completion
  // contribution timing while preserving the existing S-curve/linear choice.
  let constructionInterest = 0;
  const debtMonths = Math.max(1, lastCompletionMonth);
  let drawn = 0;
  for (let m = 1; m <= debtMonths; m++) {
    const t = m / debtMonths;
    const grossCurve = a.useScurveDrawdown ? 3 * t * t - 2 * t * t * t : t;
    const grossTarget = grossFacilityLimitDev * grossCurve;
    const target =
      contributionTiming === "completion"
        ? grossTarget
        : contributionTiming === "upfront"
          ? Math.max(0, grossTarget - operatorContributionApplied)
          : grossFacilityLimitDev > 0
            ? grossTarget * (facilityLimitDev / grossFacilityLimitDev)
            : 0;
    const avgDrawn = (drawn + target) / 2;
    constructionInterest += (avgDrawn * (baseRate + devMargin)) / 12;
    drawn = target;
  }
  const peakDebtDev =
    contributionTiming === "completion"
      ? grossFacilityLimitDev
      : facilityLimitDev;
  const debtCostDev = constructionInterest + lineFee;

  // ---- finalise DM fee + TDC (optionally fold construction finance into the fee base) ----
  const dmFeeFinance =
    (a.dmFeeIncludeLineFee ? lineFee : 0) +
    (a.dmFeeIncludeConstructionInterest ? constructionInterest : 0);
  const dmFeeBase = dmFeeBaseExFinance + dmFeeFinance;
  const devMgmtFee = resolveCostAllowance(
    devFeeInput,
    a.devManagementFeeRate,
    dmFeeBase,
  ).amount;
  const totalCost = preMgmt + devMgmtFee;
  const allInCost = totalCost + debtCostDev;
  const equityRequired = Math.max(0, totalCost - grossFacilityLimitDev);
  const fundingResidual =
    equityRequired + facilityLimitDev + operatorContributionApplied - totalCost;

  // ---- year-1 NOI & valuation ----
  // Operator amenity income (hire/sublet) — STAYS operator revenue only (P4-R3/#18: no longer cross-booked
  // into landlord NOI). The landlord's amenity return is the LL RENT the operator pays, which IS in NOI.
  const sportsIncome = a.sportsFieldAreaSqm * a.sportsFieldIncomePerSqm;
  const parkIncome = parkingIncome(a);
  const sportsFieldRent = a.sportsFieldAreaSqm * a.sportsFieldRentPerSqm;
  const parkingRent = a.parkingBays * a.parkingRentPerBay;
  const coveredPlayAreaRent = a.coveredPlayAreaGroundRent;
  const amenityRentY1 = sportsFieldRent + parkingRent + coveredPlayAreaRent;
  const noi = netRentY1 + amenityRentY1;

  // Base valuation (sell at completion — default strategy)
  const baseGrossRealisation = a.capRate > 0 ? noi / a.capRate : 0;
  const baseSellingCosts = baseGrossRealisation * a.agentsFeeRate;
  const baseNetRealisation = baseGrossRealisation - baseSellingCosts;

  // The hold-to-exit cashflows and terminal valuation share one landlord-NOI
  // schedule. This deliberately uses landlord rent (not operator EBITDA): the
  // developer earns it after PC until sale, while the terminal value capitalises
  // the exit-year run-rate.
  const landlordNoiForYear = (year: number) => {
    // loop#011: floor the online gate — the house convention everywhere else (Y1 NOI,
    // annual rent rows, monthly) — so comesOnlineYear 2.5 means "live in year 2", not 3.
    const liveNetRent = buildings.reduce((s, b) => {
      return (
        s +
        (Math.floor(b.comesOnlineYear) <= year ? b.nlaSqm * b.netRentPerSqm : 0)
      );
    }, 0);
    return (
      liveNetRent * Math.pow(1 + rentGrowthRate, year - 1) +
      amenityRentY1 * Math.pow(1 + rentGrowthRate, year - 1)
    );
  };
  const monthlyLandlordNoi = (month: number) =>
    landlordNoiForYear(Math.max(1, Math.ceil(month / 12))) / 12;

  // Exit-year valuation (S3.2 — when developerExitYear < modelYears).
  // `developerExitYear` counts PROJECT years from T0 (validate() checks it against the phased completion
  // month; the cashflow arrays sell at absolute month developerExitYear*12). `landlordNoiForYear` is
  // indexed in LEASE years from PC (its building online gate and the operator P&L rows Y1..Yn). The sale
  // therefore capitalises the run-rate of the lease year that CONTAINS the exit month — the same mapping
  // the whole-of-life loop uses via `monthlyLandlordNoi(m - months)`. An exit at the PC month values the
  // asset exactly like sell-at-PC. (Before this, the exit year was passed straight through, so the
  // construction period was double-counted as extra rent escalation and the online gate ran early.)
  const exitLeaseYear = useExitYear
    ? exitLeaseYearFor(developerExitYear, lastCompletionMonth)
    : 0;
  const exitYearNoi = useExitYear ? landlordNoiForYear(exitLeaseYear) : 0;
  const grossRealisation =
    useExitYear && a.capRate > 0
      ? exitYearNoi / a.capRate
      : baseGrossRealisation;
  const sellingCosts = grossRealisation * a.agentsFeeRate;
  const netRealisation = grossRealisation - sellingCosts;
  const profit = netRealisation - totalCost;
  const marginOnCost =
    totalCost > 0 && Number.isFinite(profit) ? profit / totalCost : Number.NaN;
  const yieldOnCost =
    totalCost > 0 && Number.isFinite(noi) ? noi / totalCost : Number.NaN;

  // Annualised developer IRR — the ungeared project cashflow books the FULL cost stack (P5-R1 /
  // CONTEXT D2). Previously it omitted the holding interest + the amenity build, so Σcf was light by
  // ~$15.9M and the IRR overstated. Scope of the Σcf = netRealisation − totalCost identity: exact
  // only at construction LVR 0 — this vector books FULL holding interest while totalCost carries
  // the equity-scaled share (pinned in model.test.ts; at LVR > 0 Σcf is light by
  // LVR × fullHoldingInterest, the debt-funded carry the geared paths charge as debtCostDev).
  // Every NON-holding totalCost component is booked exactly once and the sole inflow is
  // baseNetRealisation (== netRealisation on the base exit basis). The geared equity stream nets
  // debt draws/repayment and matches only at LVR 0 (the leverage-neutrality lock); the
  // matched-horizon exit vector below swaps the PC sale for exit-basis netRealisation plus
  // hold-period landlord NOI. Phasing: site-wide non-amenity at month 0;
  // per-building hard+soft at each completion; holding interest spread over the dev period;
  // amenity + DM fee at last completion; sale at PC.
  const months = Math.max(1, lastCompletionMonth);
  const siteWideT0 =
    landAcq +
    planningApprovalCosts +
    developerApprovalCosts +
    a.otherDdCosts +
    a.tenantRelocationCosts;
  const cf = new Array(months + 1).fill(0);
  cf[0] = -siteWideT0;
  for (const b of buildingData) {
    const bldCost =
      b.hard + b.prelim + b.margin + b.consult + b.statutory + b.contingency;
    cf[b.completionMonth] -= bldCost;
  }
  // Holding interest spread over the development period — full (equityFraction=1)
  // for the ungeared project IRR, which must be independent of financing.
  for (let m = 1; m <= months; m++) cf[m] -= fullHoldingInterest / months;
  // Site/amenity build + dev mgmt fee at last completion
  cf[months] -= siteWorksConstruction + amenityConstruction;
  cf[months] -= devMgmtFee;
  // Sale proceeds at last completion (base strategy)
  cf[months] += baseNetRealisation;
  const monthlyIrr = totalCost > 0 ? irr(cf, 0.01) : Number.NaN;
  const devIrr = Number.isFinite(monthlyIrr)
    ? Math.pow(1 + monthlyIrr, 12) - 1
    : Number.NaN;

  // Matched-horizon ungeared IRR — retain the sale-at-PC project vector above
  // as the reference metric, then extend the same construction cashflow through
  // the configured exit. Landlord NOI is credited from the first month after PC
  // through sale; the final month also receives net sale proceeds.
  let ungearedEarlyExitCf: number[] = [];
  let ungearedEarlyExitIrr = Number.NaN;
  if (useExitYear) {
    const exitMonths = developerExitYear * 12;
    ungearedEarlyExitCf = new Array(Math.max(months, exitMonths) + 1).fill(0);
    for (let m = 0; m <= months; m++) ungearedEarlyExitCf[m] = cf[m];
    // `cf` is the PC reference series and therefore includes its PC sale.
    ungearedEarlyExitCf[months] -= baseNetRealisation;
    for (let m = months + 1; m <= exitMonths; m++) {
      // month within the hold (lease month), not the absolute construction timeline
      ungearedEarlyExitCf[m] += monthlyLandlordNoi(m - months);
    }
    ungearedEarlyExitCf[exitMonths] += netRealisation;
    const uEeMonthlyIrr =
      totalCost > 0 ? irr(ungearedEarlyExitCf, 0.01) : Number.NaN;
    ungearedEarlyExitIrr = Number.isFinite(uEeMonthlyIrr)
      ? Math.pow(1 + uEeMonthlyIrr, 12) - 1
      : Number.NaN;
  }

  // Developer NPV (same monthly cashflow, monthly discount rate)
  const monthlyDiscountRate =
    Number.isFinite(a.discountRate) && a.discountRate > -1
      ? Math.pow(1 + a.discountRate, 1 / 12) - 1
      : Number.NaN;
  const developerNpv =
    totalCost > 0 && Number.isFinite(monthlyDiscountRate)
      ? npv(monthlyDiscountRate, cf)
      : Number.NaN;
  const hurdleDiff = Number.isFinite(devIrr)
    ? devIrr - a.developerHurdleIRR
    : -Infinity;
  const hurdleMet = hurdleDiff >= 0;
  let hurdleStatus: "met" | "marginal" | "below";
  if (hurdleDiff >= HURDLE_MARGIN) hurdleStatus = "met";
  else if (hurdleDiff >= -HURDLE_MARGIN) hurdleStatus = "marginal";
  else hurdleStatus = "below";

  // ---- debt model ----
  // Construction facility (constructionLvr, lineFee, constructionInterest, debtCostDev, facilityLimitDev,
  // equityRequired) and the debt-rate vars are computed above — hoisted so optional line-fee /
  // construction-interest inclusions can fold into the DM-fee base without a circular dependency.

  // Term loan — refinances construction facility at PC, holds until exit
  const termLoanLimit = baseGrossRealisation * termLvr;
  const termLoanAnnualService = termLoanLimit * (baseRate + termMargin);
  const termMonthlyService = termLoanAnnualService / 12;

  // ---- P5-R1 (CONTEXT D2): geared equity cashflows from the ungeared cost timing minus debt draws ----
  // Gross debt capacity fixes the developer-equity share. The operator
  // contribution replaces part of that debt share and is not a developer
  // cashflow receipt.
  // PLUS the whole (equity-borne) holding carry. At LVR=0 this reproduces the ungeared series exactly
  // (geared === ungeared — leverage neutrality), and as LVR rises the equity outlay shrinks while the PC
  // repayment grows, so the geared IRR responds to leverage (no longer flat/perverse). The debt slice's
  // carry is in debtCostDev only — NOT charged again as holding (consistent with D1, no double-count).
  const debtFundableBase = totalCost - holdingInterest; // holding is equity-funded (D1)
  const grossDebtDrawFraction =
    debtFundableBase > 0 ? grossFacilityLimitDev / debtFundableBase : 0;
  const equityCostFraction = 1 - grossDebtDrawFraction;
  // Fill months 0..months of `target` with the equity-funded construction outflows (mirrors the ungeared
  // `cf` cost timing, each non-holding outflow scaled by the equity fraction; holding is 100% equity).
  const fillGearedConstruction = (target: number[]) => {
    target[0] -= siteWideT0 * equityCostFraction;
    for (const b of buildingData) {
      const bldCost =
        b.hard + b.prelim + b.margin + b.consult + b.statutory + b.contingency;
      target[b.completionMonth] -= bldCost * equityCostFraction;
    }
    target[months] -=
      (siteWorksConstruction + amenityConstruction + devMgmtFee) *
      equityCostFraction;
    for (let m = 1; m <= months; m++) target[m] -= holdingInterest / months;
  };

  // Geared dev-exit IRR. When developerExitYear < modelYears the geared dev-exit cashflow IS the exit-year
  // strategy, so `gearedDevelopmentIrr` already carries the exit-year equity IRR (P9-R2 #27 removed the
  // redundant, never-displayed `exitYearIrr` field that duplicated it).
  // Geared dev-exit IRR — ALWAYS sell-at-PC (matches the types.ts "sell at PC" contract and the
  // ungeared project IRR horizon, so `geared − ungeared` is a true leverage spread). The early
  // flip (developerExitYear < modelYears) is reported separately as gearedEarlyExitIrr below.
  const gearedDevCf = new Array(months + 1).fill(0);
  fillGearedConstruction(gearedDevCf);
  // At PC: sell, repay the construction facility + its debt costs
  gearedDevCf[months] += baseNetRealisation - facilityLimitDev - debtCostDev;
  const gDevMonthlyIrr =
    equityRequired > 0 ? irr(gearedDevCf, 0.01) : Number.NaN;
  const gearedDevelopmentIrr = Number.isFinite(gDevMonthlyIrr)
    ? Math.pow(1 + gDevMonthlyIrr, 12) - 1
    : Number.NaN;

  // Geared EARLY-EXIT IRR — only when developerExitYear < modelYears: refi to the term loan at
  // PC, pay term-loan service through the hold, then sell at the exit-year valuation and repay
  // the term loan. Kept on its own field so it is never compared to the PC ungeared IRR.
  let gearedEarlyExitCf: number[] = [];
  let gearedEarlyExitIrr = Number.NaN;
  // Money multiple (MOIC convention): cash DISTRIBUTED to equity from PC onward ÷ cash CONTRIBUTED by
  // equity (construction equity plus any post-PC equity call). A post-PC leg that is negative — e.g. the
  // PC refinance when the term loan draws less than the facility + debt costs it repays, or a hold month
  // where NOI does not cover term-loan service — is a further contribution, not a negative distribution,
  // so the multiple is never pushed below zero by netting. Legs are booked term by term in lockstep with
  // the geared cashflow of the SELECTED strategy so the two can never disagree.
  let equityDistributions = 0;
  let equityContributions = equityRequired;
  const bookEquityLeg = (leg: number) => {
    if (leg >= 0) equityDistributions += leg;
    else equityContributions -= leg;
  };
  // Sell-at-PC: the PC sale net of the construction facility and its debt costs (gearedDevCf[months]).
  if (!useExitYear)
    bookEquityLeg(baseNetRealisation - facilityLimitDev - debtCostDev);
  if (useExitYear) {
    const exitMonths = developerExitYear * 12;
    gearedEarlyExitCf = new Array(Math.max(months, exitMonths) + 1).fill(0);
    fillGearedConstruction(gearedEarlyExitCf);
    // At PC: refinance construction facility into term loan (term drawn − facility repaid − debt costs)
    const pcRefinanceLeg = termLoanLimit - facilityLimitDev - debtCostDev;
    gearedEarlyExitCf[months] += pcRefinanceLeg;
    bookEquityLeg(pcRefinanceLeg);
    for (let m = months + 1; m <= exitMonths; m++) {
      // month within the hold (lease month), not the absolute construction timeline
      const interim = monthlyLandlordNoi(m - months) - termMonthlyService;
      gearedEarlyExitCf[m] += interim;
      bookEquityLeg(interim);
    }
    const exitSellingCosts = grossRealisation * a.agentsFeeRate;
    const exitLeg = grossRealisation - exitSellingCosts - termLoanLimit;
    gearedEarlyExitCf[exitMonths] += exitLeg;
    bookEquityLeg(exitLeg);
    const gEeMonthlyIrr =
      equityRequired > 0 ? irr(gearedEarlyExitCf, 0.01) : Number.NaN;
    gearedEarlyExitIrr = Number.isFinite(gEeMonthlyIrr)
      ? Math.pow(1 + gEeMonthlyIrr, 12) - 1
      : Number.NaN;

    // Multi-root / non-conventional-cashflow flag (additive): count sign changes in the early-exit
    // equity cashflow. >1 sign change ⇒ IRR may have multiple roots (e.g. extreme rent where the PC
    // refi inflow turns net-positive) → flag so callers treat developerGearedEarlyExitIrr as
    // provisional and consider MIRR. Single clean root at base rent (1 sign change) ⇒ no flag.
    const signChanges = countCashflowSignChanges(gearedEarlyExitCf);
    if (signChanges > 1) {
      flags.push({
        level: "warning",
        message: `Early-exit (Y${developerExitYear}) geared cashflow has ${signChanges} sign changes — IRR may have multiple roots; treat developerGearedEarlyExitIrr as provisional / consider MIRR at extreme rents.`,
      });
    }
  }

  // Geared whole-of-life IRR (dev builds, refinances to term loan, holds holdYears, exits by sale)
  const wlMonths = months + holdYears * 12;
  const gearedWlCf = new Array(wlMonths + 1).fill(0);
  fillGearedConstruction(gearedWlCf);
  // At PC: refinance construction facility into term loan
  // Equity cash impact = term loan drawn − facility repaid − construction debt costs already paid
  gearedWlCf[months] += termLoanLimit - facilityLimitDev - debtCostDev;
  // During the post-PC hold, the landlord receives rent and services the
  // interest-only term loan. Use the month within the hold (not the absolute
  // construction timeline) so the first 12 hold months receive Year-1 NOI and
  // the terminal value capitalises the following run-rate.
  for (let m = months + 1; m <= wlMonths; m++) {
    gearedWlCf[m] += monthlyLandlordNoi(m - months);
    gearedWlCf[m] -= termMonthlyService;
  }
  // At exit: sell at terminal cap rate (reuse capRate), repay term loan, net of selling costs.
  // Building rent AND amenity LL rent both escalate at rentGrowthRate (lease escalation). Consistent
  // with exitYearNoi above. (Operator amenity INCOME escalation is a separate lever — see P4-R5/D4.)
  // loop#011 BLOCKER fix: the terminal run-rate must be the live-set AT HOLD EXIT
  // (landlordNoiForYear(holdYears + 1) — lease year holdYears+1 is the year the terminal
  // capitalises), NOT netRentY1: the Y1 snapshot silently dropped every building coming
  // online after year 1, understating the whole-of-life terminal value on phased campuses
  // while the same cashflow's interim months booked that rent. For an all-Year-1-live
  // campus this is numerically identical to the old expression (≤1 ulp from FP
  // distributivity; measured exact on the shipped base scenario).
  const terminalNoi = landlordNoiForYear(holdYears + 1);
  const terminalGross = a.capRate > 0 ? terminalNoi / a.capRate : 0;
  const terminalSellingCosts = terminalGross * a.agentsFeeRate;
  gearedWlCf[wlMonths] += terminalGross - terminalSellingCosts - termLoanLimit;
  const gWlMonthlyIrr = equityRequired > 0 ? irr(gearedWlCf, 0.01) : Number.NaN;
  const gearedWholeOfLifeIrr = Number.isFinite(gWlMonthlyIrr)
    ? Math.pow(1 + gWlMonthlyIrr, 12) - 1
    : Number.NaN;

  const yieldOnCash =
    equityRequired > 0 && Number.isFinite(noi)
      ? noi / equityRequired
      : Number.NaN;

  // ---- operator (Reddam) annual schedule ----
  const amortYears = Math.min(a.fitoutAmortisationYears, a.leaseTermYears);
  const fitoutAmort = amortYears > 0 ? totalOpCapex / amortYears : 0;

  // Pre-compute live-building aggregates by year (avoids repeated filter/map)
  const liveCapacityByYear = new Array(a.modelYears + 1).fill(0);
  const liveNetRentByYear = new Array(a.modelYears + 1).fill(0);
  const liveOutgoingsByYear = new Array(a.modelYears + 1).fill(0);
  for (const b of buildings) {
    const startYear = Math.max(1, Math.floor(b.comesOnlineYear));
    const cap = Number.isFinite(b.enrolmentCapacity) ? b.enrolmentCapacity : 0;
    const nr =
      Number.isFinite(b.nlaSqm) && Number.isFinite(b.netRentPerSqm)
        ? b.nlaSqm * b.netRentPerSqm
        : 0;
    const og =
      Number.isFinite(b.nlaSqm) && Number.isFinite(b.outgoingsPerSqm)
        ? b.nlaSqm * b.outgoingsPerSqm
        : 0;
    for (let y = startYear; y <= a.modelYears; y++) {
      liveCapacityByYear[y] += cap;
      liveNetRentByYear[y] += nr;
      liveOutgoingsByYear[y] += og;
    }
  }

  // Loop invariants — hoisted to avoid re-computation every year
  const baseBlendedFee = blendedTuitionFee(a);
  const discountYears = Number.isFinite(a.discountDecayYears)
    ? a.discountDecayYears
    : 5;
  const foundingDisc = Number.isFinite(a.foundingFamilyDiscount)
    ? a.foundingFamilyDiscount
    : 0;
  const siblingDisc = Number.isFinite(a.siblingDiscount)
    ? a.siblingDiscount
    : 0;
  const baseDiscountFactor = Math.max(0, 1 - foundingDisc - siblingDisc);
  const ancillary = sportsIncome + parkIncome;

  let cumulative = -totalOpCapex;
  const operatorCashflows = new Array<number>(a.modelYears + 1);
  operatorCashflows[0] = -totalOpCapex;
  // P6-R1 (#7): net operating loss pool — ramp-year losses carry forward to shield later tax.
  let nolPool = 0;

  for (let y = 1; y <= a.modelYears; y++) {
    const liveCapacity = liveCapacityByYear[y];
    const liveNetRent = liveNetRentByYear[y];
    const liveOutgoings = liveOutgoingsByYear[y];

    // Allow global enrolment override (operator assumption) instead of building-derived capacity
    const capacityBase =
      a.targetEnrolment && a.targetEnrolment > 0
        ? a.targetEnrolment
        : liveCapacity;
    const enrolment = rampUtil(
      y,
      capacityBase,
      a.rampYears,
      a.steadyStateUtilisation,
      a.startingUtilisation,
    );
    // P6-R2 (#8): utilisation is measured against the capacity BASIS the enrolment ramps toward, so it
    // tracks the ramp and never exceeds steadyStateUtilisation (≤ 100%). Seat pressure (enrolment ÷ the
    // physically LIVE seats) is the separate "are we oversubscribed?" signal — it CAN exceed 100% when a
    // targetEnrolment override pushes demand above the buildings actually online. At base (no override)
    // capacityBase = liveCapacity, so utilisation === seatPressure.
    const utilisation =
      capacityBase > 0 && Number.isFinite(enrolment)
        ? enrolment / capacityBase
        : 0;
    const seatPressure =
      liveCapacity > 0 && Number.isFinite(enrolment)
        ? enrolment / liveCapacity
        : 0;
    const feeY = baseBlendedFee * Math.pow(1 + a.tuitionGrowthPct, y - 1);
    const discountFactor = y <= discountYears ? baseDiscountFactor : 1;
    const effectiveFee = feeY * discountFactor;
    const tuition = enrolment * effectiveFee;
    // P4-R5 (#13): operator hire/sublet income escalates with CPI (ancillaryGrowthPct) instead of staying
    // nominal-flat for 25 years while tuition/rent/opex grew. `ancillary` is the Y1 base.
    const ancillaryY = ancillary * Math.pow(1 + a.ancillaryGrowthPct, y - 1);
    const totalRev = tuition + ancillaryY;

    const rentFactor = Math.pow(1 + rentGrowthRate, y - 1);
    const rentNet = liveNetRent * rentFactor; // BUILDING net rent only — the rent-bridge / face-rent basis
    const outgoings = liveOutgoings * rentFactor;
    const rentGross = rentNet + outgoings; // building face rent (gross); kept amenity-free for P5/W2
    // Operator also PAYS the amenity LL rent (sports field + parking + covered) — a SEPARATE lease cost that
    // escalates with the lease. Kept OUT of rentGross so the building cost→rent bridge stays building-only
    // (P4-R2/#18), but it IS an operating cost (subtracted in EBITDA) and part of the occupancy-cost ratio.
    const amenityRent = amenityRentY1 * rentFactor;

    const opexFactor = Math.pow(1 + a.fixedOpexGrowth, y - 1);
    const variable = enrolment * a.variableCostPerStudent * opexFactor;
    const fixed = a.fixedOpex * opexFactor;
    const amort = y <= amortYears ? fitoutAmort : 0;

    // TRUE EBITDA — excludes D&A (P4-R1). Fit-out amortisation is a non-cash charge: it reduces taxable
    // income (tax shield preserved) but is added back to reach after-tax operating cash. The fit-out is a
    // cash outflow exactly once, at T0 (operatorCashflows[0] = -totalOpCapex), never again — so charging
    // it again through EBITDA→cumulative double-counted it and put an artificial step in the EBITDA bars
    // when amortisation rolled off at fitoutAmortisationYears.
    const ebitda = totalRev - rentGross - amenityRent - variable - fixed;
    const taxRate = Number.isFinite(a.corporateTaxRate)
      ? a.corporateTaxRate
      : 0;
    const taxableIncome = ebitda - amort; // amort is tax-deductible
    // P6-R1 (#7): tax loss carry-forward — the NOL pool offsets 100% of later positive taxable income
    // (AU-standard: indefinite carryforward, no carryback). Ramp-year losses are no longer wasted.
    const taxableAfterNol =
      taxableIncome > 0 ? Math.max(0, taxableIncome - nolPool) : 0;
    if (taxableIncome < 0)
      nolPool += -taxableIncome; // accumulate the loss
    else nolPool = Math.max(0, nolPool - taxableIncome); // draw down by the profit absorbed
    const tax = taxableAfterNol * taxRate;
    const operatingCashflow = ebitda - tax; // after-tax operating cash (amort added back)
    const netIncome = ebitda - amort - tax; // accrual net income (P&L net income row)
    cumulative += operatingCashflow;
    operatorCashflows[y] = operatingCashflow;

    // Occupancy-cost ratio = TOTAL lease cost (building rent + outgoings + amenity rent) / revenue.
    const totalRentCost = rentGross + amenityRent;

    years[y - 1] = {
      year: y,
      enrolment,
      utilisation,
      seatPressure,
      tuitionRevenue: tuition,
      headlineFee: feeY,
      effectiveFee,
      ancillaryRevenue: ancillaryY,
      totalRevenue: totalRev,
      rentNet,
      outgoings,
      rentGross,
      amenityRent,
      variableCosts: variable,
      fixedOpex: fixed,
      fitoutAmortisation: amort,
      tax,
      reddamEbitda: ebitda,
      reddamNetIncome: netIncome,
      reddamOperatingCashflow: operatingCashflow,
      cumulativeCashflow: cumulative,
      rentPctRevenue:
        totalRev > 0 && Number.isFinite(totalRentCost)
          ? totalRentCost / totalRev
          : 0,
    };
  }

  // ---- operator KPIs ----
  const opNpv = npv(a.discountRate, operatorCashflows);
  const opIrr = irr(operatorCashflows, 0.08);
  const totalTax = sum(years.map((y) => y.tax));
  let paybackYear: number | null = null;
  let cum = -totalOpCapex;
  for (const yr of years) {
    cum += yr.reddamOperatingCashflow; // payback is a cash-recovery measure (amort added back), not accrual
    if (cum >= 0) {
      paybackYear = yr.year;
      break;
    }
  }
  const yearOneEbitda = years[0]?.reddamEbitda ?? 0;
  const avgEbitda = years.length
    ? sum(years.map((y) => y.reddamEbitda)) / years.length
    : 0;
  // Lease incentive (W2) — pro-rata decomposition of face rent; defaults to no-op when both inputs = 0
  // Use first year with non-zero rentGross as the basis — avoids silent zero when all buildings are deferred.
  const firstRentYear = years.find((y) => y.rentGross > 0);
  const rentFreeEquiv =
    ((firstRentYear?.rentGross ?? 0) / 12) * (a.rentFreeMonths ?? 0);
  const leaseIncentivePvTotal = (a.leaseIncentiveLumpSum ?? 0) + rentFreeEquiv;
  // Clamp to at least 1 year — avoids divide-by-zero when incentiveAmortisationYears is 0 or nullish.
  const amortYearsIncentive = Math.max(
    1,
    a.incentiveAmortisationYears ?? a.leaseTermYears,
  );
  const incentiveAmortisedPerYear = leaseIncentivePvTotal / amortYearsIncentive;
  const faceRentY1 = years[0]?.rentGross ?? 0;
  const totalRevY1 = years[0]?.totalRevenue ?? 0;
  const effectiveRentY1 = faceRentY1 - incentiveAmortisedPerYear;
  const effectiveRentPctRevenueY1 =
    totalRevY1 > 0 ? effectiveRentY1 / totalRevY1 : 0;
  // Amenity-inclusive effective occupancy cost: same basis as the authoritative rentPctRevenueYear1
  // (totalRentCost = rentGross + amenityRent), less the amortised incentive. The building-only
  // effectiveRentPctRevenueY1 above omits amenity landlord rent and must never be graded against
  // RENT_PCT_REVENUE_THRESHOLD/CAUTION — an amenity-heavy lease would read "good" while the
  // authoritative ratio reads "bad".
  const effectiveOccupancyCostY1 =
    faceRentY1 + (years[0]?.amenityRent ?? 0) - incentiveAmortisedPerYear;
  const effectiveOccupancyCostPctRevenueY1 =
    totalRevY1 > 0 ? effectiveOccupancyCostY1 / totalRevY1 : 0;
  // Ramp survivability (W5)
  const negativeEbitdaYears = years.filter((y) => y.reddamEbitda < 0).length;
  // Include the T=0 pre-income position (-totalOpCapex) as the seed — years[0].cumulativeCashflow
  // already incorporates Y1 income, so the opening trough is otherwise never captured.
  const maxCumulativeDrawdown = Math.min(
    -totalOpCapex,
    ...(years.length > 0 ? years.map((y) => y.cumulativeCashflow) : [0]),
  );
  const reservesRequiredToSurvive =
    maxCumulativeDrawdown < 0 ? -maxCumulativeDrawdown : 0;
  const steadyRevenue = years.at(-1)?.totalRevenue ?? 0;
  const rentPctY1 = years[0]?.rentPctRevenue ?? 0;
  const rentPctAvg = years.length
    ? sum(years.map((y) => y.rentPctRevenue)) / years.length
    : 0;

  // ---- flags ----
  if (totalCost <= 0)
    flags.push({
      level: "error",
      message:
        "Total developer cost is zero — check purchase price and construction inputs.",
    });
  if (a.capRate <= 0)
    flags.push({
      level: "error",
      message: "Cap rate must be positive for valuation.",
    });
  if (equityRequired <= 0)
    flags.push({
      level: "warning",
      message:
        "100% (or greater) debt financing — equity required is zero or negative.",
    });
  if (
    !Number.isFinite(devIrr) ||
    !Number.isFinite(gearedDevelopmentIrr) ||
    !Number.isFinite(gearedWholeOfLifeIrr) ||
    !Number.isFinite(opIrr)
  ) {
    flags.push({
      level: "warning",
      message: "IRR could not be computed for the current cashflow profile.",
    });
  }
  if (rentPctY1 > RENT_PCT_REVENUE_THRESHOLD)
    flags.push({
      level: "warning",
      message: `Year-1 rent is ${(rentPctY1 * 100).toFixed(1)}% of revenue (target ≤ ${(RENT_PCT_REVENUE_THRESHOLD * 100).toFixed(0)}%).`,
    });
  // The raw path deliberately leaves the financing-reference annotation unavailable. The bounded
  // outer computeCore wrapper derives it from repeated raw evaluations without recursive solves.
  const gearedHurdleMet = false;
  const gearedHurdleStatus = "na" as const;

  // Primary developer return = the matched-horizon UNGEARED project return. Financing diagnostics
  // remain secondary and cannot change the operator-facing hurdle verdict.
  const developerPrimaryGearedIrr = useExitYear
    ? gearedEarlyExitIrr
    : gearedDevelopmentIrr;
  const developerPrimaryIrr = useExitYear ? ungearedEarlyExitIrr : devIrr;
  const developerPrimaryHurdleDiff = Number.isFinite(developerPrimaryIrr)
    ? developerPrimaryIrr - a.developerHurdleIRR
    : -Infinity;
  const developerPrimaryHurdleMet = developerPrimaryHurdleDiff >= 0;
  let developerPrimaryHurdleStatus: "met" | "marginal" | "below";
  if (developerPrimaryHurdleDiff >= HURDLE_MARGIN)
    developerPrimaryHurdleStatus = "met";
  else if (developerPrimaryHurdleDiff >= -HURDLE_MARGIN)
    developerPrimaryHurdleStatus = "marginal";
  else developerPrimaryHurdleStatus = "below";
  if (!developerPrimaryHurdleMet)
    flags.push({
      level: "warning",
      message: `Matched-horizon ungeared IRR ${Number.isFinite(developerPrimaryIrr) ? (developerPrimaryIrr * 100).toFixed(1) + "%" : "n/a"} below hurdle ${(a.developerHurdleIRR * 100).toFixed(1)}%.`,
    });
  if (!Number.isFinite(opNpv) || opNpv < 0)
    flags.push({
      level: "error",
      message: `${a.operatorName} NPV is ${Number.isFinite(opNpv) ? "negative" : "not computable"} at the stated discount rate.`,
    });
  if (yearOneEbitda < 0)
    flags.push({
      level: "warning",
      message: "Year-1 EBITDA is negative — ramp depth may be too steep.",
    });

  const stageTotal =
    a.earlyLearningShare + a.primaryShare + a.secondaryShare + a.seniorShare;
  // A negative share is an input error in its own right — normalisation can mask it
  // (compensating shares keep the total at 100%), so it must be flagged explicitly.
  // A share above 1 is the same class on non-gated compute paths (direct compute /
  // realtime merge never see validate()): after normalisation that stage alone
  // dominates the mix.
  const stageShareEntries: Array<[string, number]> = [
    ["Early learning", a.earlyLearningShare],
    ["Primary", a.primaryShare],
    ["Secondary", a.secondaryShare],
    ["Senior", a.seniorShare],
  ];
  for (const [stageName, share] of stageShareEntries) {
    if (!Number.isFinite(share)) {
      // NaN passes both < 0 and > 1 comparisons — checked first, explicitly.
      flags.push({
        level: "warning",
        message: `${stageName} tuition-stage share is not a finite number — it contributes 0% after normalisation; likely an input error.`,
      });
    } else if (share < 0) {
      flags.push({
        level: "warning",
        message: `${stageName} tuition-stage share is negative (${(share * 100).toFixed(1)}%) — after normalisation this stage contributes 0% and the remaining stages are scaled up; a negative share is likely an input error.`,
      });
    } else if (share > 1) {
      flags.push({
        level: "warning",
        message: `${stageName} tuition-stage share is above 100% (${(share * 100).toFixed(1)}%) — a single stage cannot exceed 100% of students; normalisation rescales every stage around it, distorting the whole mix. Likely an input error.`,
      });
    }
  }
  if (Math.abs(stageTotal - 1) > 0.02) {
    flags.push({
      level: "info",
      message: `Tuition-stage mix totals ${(stageTotal * 100).toFixed(1)}%; shares are normalised in calculations.`,
    });
  }
  const parkingAlloc =
    a.weekdayParentParkingBays +
    a.weekendSportsParkingBays +
    a.staffReservedParkingBays;
  if (parkingAlloc > a.parkingBays) {
    flags.push({
      level: "warning",
      message: `Parking allocation uses ${parkingAlloc.toLocaleString("en-AU")} bays against ${a.parkingBays.toLocaleString("en-AU")} physical bays.`,
    });
  }
  const latestBuilding = Math.max(
    1,
    ...buildings.map((b) => b.comesOnlineYear),
  );
  if (latestBuilding > a.rampYears) {
    flags.push({
      level: "info",
      message: `Latest building opens in Y${latestBuilding}; ramp may need to extend beyond ${a.rampYears} years.`,
    });
  }
  if (
    Number.isFinite(a.coveredPlayAreaGroundRent) &&
    a.coveredPlayAreaGroundRent > 0 &&
    (a.coveredPlayAreaConstructionCost ?? 0) <= 0
  ) {
    flags.push({
      level: "warning",
      message:
        "Covered play area ground rent is set without a covered play area build cost — rent may not be value-backed.",
    });
  }

  // Working capital / liquidity check (post-capex operating position)
  const minReserve = Number.isFinite(a.minimumCashReserveMonths)
    ? (a.minimumCashReserveMonths * a.fixedOpex) / 12
    : 0;
  const operatingMin =
    years.length > 1
      ? Math.min(...years.slice(1).map((y) => y.cumulativeCashflow))
      : Number.NaN;
  if (Number.isFinite(operatingMin) && operatingMin < -minReserve) {
    // A4: compactCurrency is the flags/checks read for magnitudes; the funding-residual
    // fail evidence is the documented exception (preciseCurrency — cents matter there).
    flags.push({
      level: "info",
      message: `Peak operating funding deficit ${compactCurrency(Math.abs(operatingMin))} exceeds the ${a.minimumCashReserveMonths}-month reserve (${compactCurrency(minReserve)}).`,
    });
  }

  const noDebt = termLoanAnnualService <= 0;
  const dscr = noDebt ? 0 : noi / termLoanAnnualService;
  const dscrStatus = noDebt
    ? ("na" as const)
    : dscr >= 1.25
      ? ("good" as const)
      : dscr >= 1.0
        ? ("warn" as const)
        : ("bad" as const);

  const developer: Developer = {
    purchasePrice: a.purchasePrice,
    stampDuty,
    acquisitionFee,
    hardConstruction,
    siteWorksConstruction,
    loadedConstructionTotal,
    costBasisMode,
    preliminaries,
    builderMargin,
    consultantCosts,
    statutoryCosts,
    planningApprovalCosts,
    constructionContingency: devContingency,
    holdingInterest,
    developmentManagementFee: devMgmtFee,
    dmFeeBase,
    otherDdCosts: a.otherDdCosts,
    tenantRelocationCosts: a.tenantRelocationCosts,
    totalCost,
    allInCost,
    netRentYear1: netRentY1,
    noi,
    grossRealisation,
    sellingCosts,
    netRealisation,
    profit,
    marginOnCost,
    irr: devIrr,
    hurdleMet,
    hurdleStatus,
    sportsIncome,
    parkingIncome: parkIncome,
    sportsFieldRent,
    parkingRent,
    coveredPlayAreaRent,
    amenityConstruction,
    yieldOnCost,
    grossFacilityLimitDev,
    facilityLimitDev,
    peakDebtDev,
    operatorContributionApplied,
    equityRequired,
    fundingResidual,
    debtCostDev,
    constructionInterest,
    lineFee,
    termLoanLimit,
    termLoanAnnualService,
    gearedDevelopmentIrr,
    ungearedEarlyExitIrr,
    gearedEarlyExitIrr,
    gearedWholeOfLifeIrr,
    gearedHurdleMet,
    gearedHurdleStatus,
    yieldOnCash,
    // P5-R2 (root #3): TRUE equity multiple (MOIC) — distributions to equity of the selected strategy ÷
    // equity contributed (construction equity + any post-PC equity call). Sell-at-PC: PC sale −
    // construction facility − its debt costs. Early exit: PC refinance leg + interim net rent after
    // term-loan service + exit sale net of the term loan (the same legs gearedEarlyExitCf books). The
    // former formula mixed the exit-year sale with the PC-vintage facility repayment — a cashflow the
    // model never books. (Before P5-R2 it was `netRealisation / equityRequired`, ~3.6x → ~1.3x at base.)
    moneyMultiple:
      equityRequired > 0 &&
      Number.isFinite(equityDistributions) &&
      Number.isFinite(equityContributions) &&
      equityContributions > 0
        ? equityDistributions / equityContributions
        : Number.NaN,
    developerApprovalShare: developerApprovalCosts,
    reddamApprovalShare,
    lastCompletionMonth,
    cashflowUngeared: cf,
    cashflowUngearedEarlyExit: ungearedEarlyExitCf,
    cashflowGearedDevExit: gearedDevCf,
    cashflowGearedEarlyExit: gearedEarlyExitCf,
    cashflowGearedWholeOfLife: gearedWlCf,
    gearedHurdle: Number.NaN,
    gearedHurdleConverged: false,
    dscr,
    dscrStatus,
    leaseIncentiveCost: leaseIncentivePvTotal,
  };

  const kpis: KPIs = {
    reddamNpv: opNpv,
    reddamIrr: Number.isFinite(opIrr) ? opIrr : null,
    paybackYear,
    cumulativeCashflowYearN: years.at(-1)?.cumulativeCashflow ?? 0,
    avgEbitda,
    yearOneEbitda,
    rentPctRevenueYear1: rentPctY1,
    rentPctRevenueAvg: rentPctAvg,
    rentPctRevenueSteadyState: years.at(-1)?.rentPctRevenue ?? 0, // P1-R1 — final-modelled-year occupancy-cost ratio (cheap, no solver)
    breakEvenRentY1: 0, // P1-R2 — placeholder; solver value assembled in compute() wrapper
    breakEvenEnrolment: 0, // P1-R3 — placeholder; solver value assembled in compute() wrapper
    breakEvenRentConverged: false, // P1 hardening — placeholder; real flag assembled in compute() wrapper
    breakEvenEnrolmentConverged: false, // P1 hardening — placeholder; real flag assembled in compute() wrapper
    developerIrr: devIrr,
    developerUngearedEarlyExitIrr: ungearedEarlyExitIrr,
    developerNpv,
    developerMarginOnCost: marginOnCost,
    developerYieldOnCost: yieldOnCost,
    developerHurdleMet: hurdleMet,
    developerHurdleStatus: hurdleStatus,
    developerGearedIrr: gearedDevelopmentIrr,
    developerGearedEarlyExitIrr: gearedEarlyExitIrr,
    developerGearedWholeOfLifeIrr: gearedWholeOfLifeIrr,
    developerGearedHurdleMet: gearedHurdleMet,
    developerGearedHurdleStatus: gearedHurdleStatus,
    developerPrimaryGearedIrr,
    developerPrimaryIrr,
    developerPrimaryHurdleMet,
    developerPrimaryHurdleStatus,
    developerYieldOnCash: yieldOnCash,
    developerEquityRequired: equityRequired,
    developerMoneyMultiple:
      equityRequired > 0 &&
      Number.isFinite(equityDistributions) &&
      Number.isFinite(equityContributions) &&
      equityContributions > 0
        ? equityDistributions / equityContributions
        : Number.NaN,
    steadyStateRevenue: steadyRevenue,
    totalTax,
    negativeEbitdaYears,
    maxCumulativeDrawdown,
    reservesRequiredToSurvive,
    leaseIncentivePvTotal,
    incentiveAmortisedPerYear,
    effectiveRentY1,
    effectiveRentPctRevenueY1,
    effectiveOccupancyCostY1,
    effectiveOccupancyCostPctRevenueY1,
  };

  return {
    years,
    developer,
    campus: {
      totalNlaSqm: totalNla,
      totalGfaSqm: totalGfa,
      totalCapacity: totalCap,
      totalEnrolmentCapacity: totalCap,
      totalReddamCapex: totalOpCapex,
      totalYear1NetRent: netRentY1,
      totalYear1Outgoings: outgoingsY1,
      blendedFeeYear1: blendedTuitionFee(a),
    },
    kpis,
    flags,
  };
}

type DeveloperRentMetric =
  | "developerIrr"
  | "developerUngearedEarlyExitIrr"
  | "developerGearedIrr"
  | "developerGearedEarlyExitIrr"
  | "developerGearedWholeOfLifeIrr"
  | "developerYieldOnCost"
  | "developerMarginOnCost";

function matchedDeveloperIrrMetrics(scenario: Scenario): {
  ungeared: "developerIrr" | "developerUngearedEarlyExitIrr";
  geared: "developerGearedIrr" | "developerGearedEarlyExitIrr";
  gearedCashflow: "cashflowGearedDevExit" | "cashflowGearedEarlyExit";
} {
  const exitYear =
    scenario.assumptions.developerExitYear ?? scenario.assumptions.modelYears;
  return exitYear < scenario.assumptions.modelYears
    ? {
        ungeared: "developerUngearedEarlyExitIrr",
        geared: "developerGearedEarlyExitIrr",
        gearedCashflow: "cashflowGearedEarlyExit",
      }
    : {
        ungeared: "developerIrr",
        geared: "developerGearedIrr",
        gearedCashflow: "cashflowGearedDevExit",
      };
}

function computeCore(scenario: Scenario): ModelResult {
  const result = computeCoreRaw(scenario);
  const metrics = matchedDeveloperIrrMetrics(scenario);
  const canCalibrate =
    Array.isArray(scenario.buildings) && scenario.buildings.length > 0;
  const derived = canCalibrate
    ? deriveGearedEquivalentHurdle(
        scenario.assumptions.developerHurdleIRR,
        (rentScale) => {
          const trial = clone(scenario);
          for (const building of trial.buildings) {
            if (Number.isFinite(building.netRentPerSqm)) {
              building.netRentPerSqm *= rentScale;
            }
          }
          const raw = computeCoreRaw(trial);
          return {
            ungearedIrr: raw.kpis[metrics.ungeared],
            gearedIrr: raw.kpis[metrics.geared],
            gearedCashflowRootAmbiguous:
              countCashflowSignChanges(raw.developer[metrics.gearedCashflow]) >
              1,
          };
        },
      )
    : {
        hurdleIrr: Number.NaN,
        rentScale: Number.NaN,
        achievedUngearedIrr: Number.NaN,
        converged: false,
        rootAmbiguous: false,
      };

  // Explicit leverage-neutrality lock: only genuinely unlevered matched cashflows publish the
  // stated project hurdle exactly. Positive term leverage remains cashflow-derived, including Y5.
  const gearedHurdle =
    derived.converged &&
    result.developer.facilityLimitDev <= 0 &&
    result.developer.termLoanLimit <= 0
      ? scenario.assumptions.developerHurdleIRR
      : derived.hurdleIrr;
  const matchedGearedIrr = result.kpis[metrics.geared];
  const gearedHurdleDiff =
    derived.converged && Number.isFinite(matchedGearedIrr)
      ? matchedGearedIrr - gearedHurdle
      : Number.NaN;
  const gearedHurdleMet =
    Number.isFinite(gearedHurdleDiff) && gearedHurdleDiff >= 0;
  const gearedHurdleStatus = !Number.isFinite(gearedHurdleDiff)
    ? ("na" as const)
    : gearedHurdleDiff >= HURDLE_MARGIN
      ? ("met" as const)
      : gearedHurdleDiff >= -HURDLE_MARGIN
        ? ("marginal" as const)
        : ("below" as const);

  result.developer.gearedHurdle = gearedHurdle;
  result.developer.gearedHurdleConverged = derived.converged;
  result.developer.gearedHurdleMet = gearedHurdleMet;
  result.developer.gearedHurdleStatus = gearedHurdleStatus;
  result.kpis.developerGearedHurdleMet = gearedHurdleMet;
  result.kpis.developerGearedHurdleStatus = gearedHurdleStatus;

  if (derived.converged && derived.rootAmbiguous) {
    result.flags.push({
      level: "warning",
      message:
        "Cashflow-equivalent geared reference is root-ambiguous at the calibrated rent — treat this secondary financing diagnostic as provisional and consider MIRR/NPV.",
    });
  }

  if (
    derived.converged &&
    Number.isFinite(matchedGearedIrr) &&
    !gearedHurdleMet
  ) {
    result.flags.push({
      level: "warning",
      message: `Matched-horizon geared IRR ${Number.isFinite(matchedGearedIrr) ? (matchedGearedIrr * 100).toFixed(1) + "%" : "n/a"} below cashflow-equivalent geared reference ${(gearedHurdle * 100).toFixed(1)}%.`,
    });
  }

  return result;
}

/** Derive the matched-horizon financing reference without running the operator break-even solvers.
 *  Intended for background risk requests that need one annotated baseline plus many raw outcomes. */
export function computeGearedReferenceBaseline(
  scenario: Scenario,
): ModelResult {
  return computeCore(scenario);
}

/** Public engine entry point. Runs the non-recursive core, then the break-even bisection
 *  solvers, and assembles the operator break-even KPIs (+ their convergence flags) onto the
 *  result. rentPctRevenueSteadyState is assembled in computeCore (cheap, no solver). Internal
 *  hot-loop callers (tornado / monteCarlo / solveRentForHurdle) call computeCore directly so
 *  they never pay for the two ~60-iteration solvers. */
export function compute(scenario: Scenario): ModelResult {
  const result = computeCore(scenario);
  const breakEvenRentResult = breakEvenRent(scenario);
  const breakEvenEnrolmentResult = breakEvenEnrolment(scenario);
  result.kpis = {
    ...result.kpis,
    breakEvenRentY1: breakEvenRentResult.breakEvenRentY1,
    breakEvenEnrolment: breakEvenEnrolmentResult.enrolment,
    // Thread the convergence flags so render sites can caveat fallback values instead of
    // showing a fabricated break-even (e.g. Downside: EBITDA never reaches 0 → fallback shown).
    breakEvenRentConverged: breakEvenRentResult.converged,
    breakEvenEnrolmentConverged: breakEvenEnrolmentResult.converged,
  };
  return result;
}

/** Bisection inverse-solver: find uniform rent-scale that makes a target metric hit `target`.
 *  Reused by computeRentDecomposition (calibration column) and rentMultiplier. */
export function solveRentForHurdle(
  scenario: Scenario,
  metric: DeveloperRentMetric,
  target: number,
): { rentScale: number; achieved: number; converged: boolean } {
  if (
    !Number.isFinite(target) ||
    !Array.isArray(scenario.buildings) ||
    scenario.buildings.length === 0
  ) {
    return { rentScale: 1, achieved: Number.NaN, converged: false };
  }
  const evaluate = (scale: number): number => {
    const trial = clone(scenario);
    for (const b of trial.buildings) {
      if (Number.isFinite(b.netRentPerSqm))
        b.netRentPerSqm = b.netRentPerSqm * scale;
    }
    // Bisection hot loop (≤25 calls): `metric` is always a core KPI, so use computeCore and
    // skip the break-even solvers the public compute() wrapper would run on every iteration.
    const r = computeCoreRaw(trial);
    return r.kpis[metric] as number;
  };
  let lo = 0.1,
    hi = 10;
  let fLo = evaluate(lo);
  let fHi = evaluate(hi);
  // Expand search if bracket edges are non-finite. Remember the last non-finite
  // scale on each side: the metric (e.g. geared IRR) is undefined below some rent
  // scale, and a ×2 step can leap from "undefined" straight past the root.
  let loInfeasible = Number.NaN;
  let hiInfeasible = Number.NaN;
  if (!Number.isFinite(fLo)) {
    for (let attempt = 0; attempt < 8; attempt++) {
      loInfeasible = lo;
      lo = lo * 2;
      fLo = evaluate(lo);
      if (Number.isFinite(fLo)) break;
    }
  }
  if (!Number.isFinite(fHi)) {
    for (let attempt = 0; attempt < 8; attempt++) {
      hiInfeasible = hi;
      hi = hi / 2;
      fHi = evaluate(hi);
      if (Number.isFinite(fHi)) break;
    }
  }
  if (!Number.isFinite(fLo) || !Number.isFinite(fHi)) {
    return { rentScale: 1, achieved: Number.NaN, converged: false };
  }
  if ((fLo - target) * (fHi - target) > 0) {
    // loop#035: the root may sit between the last non-finite scale and the first
    // finite one (RevG basis: geared IRR is NaN at 0.4, 0.655 at 0.8, target 0.481).
    // Walk the crossed edge back toward its feasibility boundary with a finite-side
    // bisection; adopt the boundary as the new edge if it brackets the target.
    const refineEdge = (
      infeasible: number,
      feasible: number,
      feasibleValue: number,
    ): { scale: number; value: number } => {
      let bad = infeasible;
      let good = feasible;
      let value = feasibleValue;
      for (let i = 0; i < 12; i++) {
        const mid = (bad + good) / 2;
        const f = evaluate(mid);
        if (Number.isFinite(f)) {
          good = mid;
          value = f;
        } else {
          bad = mid;
        }
      }
      return { scale: good, value };
    };
    if (Number.isFinite(loInfeasible)) {
      const edge = refineEdge(loInfeasible, lo, fLo);
      if ((edge.value - target) * (fHi - target) <= 0) {
        lo = edge.scale;
        fLo = edge.value;
      }
    }
    if ((fLo - target) * (fHi - target) > 0 && Number.isFinite(hiInfeasible)) {
      const edge = refineEdge(hiInfeasible, hi, fHi);
      if ((fLo - target) * (edge.value - target) <= 0) {
        hi = edge.scale;
        fHi = edge.value;
      }
    }
    if ((fLo - target) * (fHi - target) > 0) {
      return { rentScale: 1, achieved: evaluate(1), converged: false };
    }
  }
  let mid = 1;
  for (let i = 0; i < 25; i++) {
    mid = (lo + hi) / 2;
    const f = evaluate(mid);
    if (Math.abs(f - target) < 1e-6) break;
    if ((f - target) * (fLo - target) < 0) hi = mid;
    else lo = mid;
  }
  const achieved = evaluate(mid);
  return {
    rentScale: mid,
    achieved,
    converged: Math.abs(achieved - target) < 1e-3,
  };
}

// Break-even solvers (breakEvenRent / breakEvenEnrolment) are now imported from
// ./solvers/break-even — see import at top of file. They call computeCore via the
// _setComputeCore injection point (wired below).

function attributeRentToComponents(
  rentY1: number,
  dev: Developer,
  a: Assumptions,
  outgoings: number,
): {
  key: RentDecompositionComponent["key"];
  label: string;
  share: number;
  amount: number;
}[] {
  if (rentY1 === 0 || dev.totalCost === 0) return [];
  const leaseYears = Math.max(1, a.leaseTermYears);
  const landAnnualised =
    (dev.purchasePrice + dev.stampDuty + dev.acquisitionFee) / leaseYears;
  const debtAnnualised = (dev.debtCostDev + dev.holdingInterest) / leaseYears;
  const equityFormulation = a.equityReturnFormulation ?? "perpetuity";
  const equityAnnualised =
    equityFormulation === "amortised" && a.developerHurdleIRR > 0
      ? (dev.equityRequired * a.developerHurdleIRR) /
        (1 - Math.pow(1 + a.developerHurdleIRR, -leaseYears))
      : dev.equityRequired * a.developerHurdleIRR;
  const marginAnnualised = Math.max(0, dev.profit) / leaseYears;
  const softAnnualised =
    (dev.consultantCosts +
      dev.statutoryCosts +
      dev.planningApprovalCosts +
      dev.constructionContingency +
      dev.developmentManagementFee +
      dev.otherDdCosts +
      dev.tenantRelocationCosts) /
    leaseYears;
  const approvalRecoveryAnnualised = dev.developerApprovalShare / leaseYears;
  const opexY1 = outgoings;
  const basis =
    landAnnualised +
    debtAnnualised +
    equityAnnualised +
    marginAnnualised +
    softAnnualised +
    opexY1 +
    approvalRecoveryAnnualised;
  if (basis === 0) return [];
  const items = [
    {
      key: "construction_debt_service" as const,
      label: "Construction debt + holding interest",
      basis: debtAnnualised,
    },
    {
      key: "equity_return" as const,
      label: "Developer equity return @ hurdle IRR",
      basis: equityAnnualised,
    },
    {
      key: "developer_margin" as const,
      label: "Developer profit / margin",
      basis: marginAnnualised,
    },
    {
      key: "land_recovery" as const,
      label: "Land acquisition recovery",
      basis: landAnnualised,
    },
    {
      key: "soft_costs" as const,
      label: "Soft costs (consultants, statutory, mgmt fee)",
      basis: softAnnualised,
    },
    {
      key: "opex_recovery" as const,
      label: "Outgoings / opex pass-through",
      basis: opexY1,
    },
    {
      key: "approval_recovery" as const,
      label: "Approval cost recovery (developer share)",
      basis: approvalRecoveryAnnualised,
    },
  ];
  return items.map((it) => ({
    key: it.key,
    label: it.label,
    share: it.basis / basis,
    amount: (it.basis / basis) * rentY1,
  }));
}

function lifetimeRentNpv(rentY1: number, a: Assumptions): number {
  const r = Number.isFinite(a.discountRate) ? a.discountRate : 0;
  const g = effectiveRentGrowth(a);
  let s = 0;
  for (let y = 1; y <= a.leaseTermYears; y++) {
    s += (rentY1 * Math.pow(1 + g, y - 1)) / Math.pow(1 + r, y);
  }
  return s;
}

export function computeRentDecomposition(
  scenario: Scenario,
  targetMetric?:
    | "developerGearedIrr"
    | "developerGearedEarlyExitIrr"
    | "developerGearedWholeOfLifeIrr"
    | "developerYieldOnCost"
    | "developerMarginOnCost",
): RentDecomposition {
  const a = scenario.assumptions;
  // Default hurdle-floor target follows the developer's actual horizon: early-exit geared when
  // developerExitYear < modelYears, else sell-at-PC geared. Explicit param still overrides (solvers).
  const exitYear = a.developerExitYear ?? a.modelYears;
  const useExitYear = exitYear < a.modelYears;
  const resolvedMetric =
    targetMetric ??
    (useExitYear ? "developerGearedEarlyExitIrr" : "developerGearedIrr");
  // Reads only .developer/.years (core); use computeCore to skip the break-even solvers (perf).
  const baseline = computeCore(scenario);
  const dev = baseline.developer;
  const scenarioRentY1 = baseline.years[0]?.rentGross ?? 0;
  const outgoings = baseline.years[0]?.outgoings ?? 0;

  // A matched geared metric always uses the cashflow-derived reference. This remains true when the
  // construction facility is zero but Y5 term leverage is positive; bare-hurdle substitution would
  // break the central same-rent reconciliation invariant. NaN propagates as unavailable.
  const matchedGearedMetric = matchedDeveloperIrrMetrics(scenario).geared;
  const targetsMatchedDerivedReference = resolvedMetric === matchedGearedMetric;
  const target = targetsMatchedDerivedReference
    ? dev.gearedHurdleConverged
      ? dev.gearedHurdle
      : Number.NaN
    : a.developerHurdleIRR;
  const solver = Number.isFinite(target)
    ? solveRentForHurdle(scenario, resolvedMetric, target)
    : { rentScale: Number.NaN, achieved: Number.NaN, converged: false };
  const calibScenario = clone(scenario);
  if (solver.converged) {
    for (const b of calibScenario.buildings) {
      if (Number.isFinite(b.netRentPerSqm))
        b.netRentPerSqm = b.netRentPerSqm * solver.rentScale;
    }
  }
  const calibResult = computeCoreRaw(calibScenario); // reads only .years/.developer (raw core)
  const calibRentY1 = solver.converged
    ? (calibResult.years[0]?.rentGross ?? 0)
    : scenarioRentY1;
  const calibOutgoings = calibResult.years[0]?.outgoings ?? outgoings;

  const scenarioParts = attributeRentToComponents(
    scenarioRentY1,
    dev,
    a,
    outgoings,
  );
  const calibParts = attributeRentToComponents(
    calibRentY1,
    calibResult.developer,
    a,
    calibOutgoings,
  );

  const components: RentDecompositionComponent[] = scenarioParts.map(
    (sp, i) => {
      const cp = calibParts[i] ?? { amount: 0, share: 0 };
      return {
        key: sp.key,
        label: sp.label,
        scenarioAmount: sp.amount,
        calibratedAmount: cp.amount,
        gapAmount: cp.amount - sp.amount,
        scenarioShareOfRent: sp.share,
        calibratedShareOfRent: cp.share,
      };
    },
  );

  return {
    scenarioRentY1,
    calibratedRentY1: calibRentY1,
    gapRentY1: calibRentY1 - scenarioRentY1,
    scenarioLifetimeNpv: lifetimeRentNpv(scenarioRentY1, a),
    calibratedLifetimeNpv: lifetimeRentNpv(calibRentY1, a),
    components,
    rentTargetMetric: resolvedMetric,
    calibratedSolverConverged: solver.converged,
  };
}

/** Cost build-up view of the Rent Bridge (board-pack C-redesign).
 *  Each line is the UNSCALED annualised cost basis (vs attributeRentToComponents which pro-rata
 *  scales to the rent). cost-recovery = Σ lines (the developer's hurdle break-even rent);
 *  margin/overreach = scenarioRent − cost-recovery (the residual read as over-asking).
 *  ADDITIVE: does not modify compute/computeCore/attributeRentToComponents/computeRentDecomposition. */
export function computeRentBuildUp(scenario: Scenario): RentBuildUp {
  const result = computeCoreRaw(scenario);
  const dev = result.developer;
  const a = scenario.assumptions;
  const leaseYears = Math.max(1, a.leaseTermYears);
  const scenarioRentY1 = result.years[0]?.rentGross ?? 0;
  const opexY1 = result.years[0]?.outgoings ?? 0;

  const land =
    (dev.purchasePrice + dev.stampDuty + dev.acquisitionFee) / leaseYears;
  const debt = (dev.debtCostDev + dev.holdingInterest) / leaseYears;
  const equityFormulation = a.equityReturnFormulation ?? "perpetuity";
  const equity =
    equityFormulation === "amortised" && a.developerHurdleIRR > 0
      ? (dev.equityRequired * a.developerHurdleIRR) /
        (1 - Math.pow(1 + a.developerHurdleIRR, -leaseYears))
      : dev.equityRequired * a.developerHurdleIRR;
  const soft =
    (dev.consultantCosts +
      dev.statutoryCosts +
      dev.planningApprovalCosts +
      dev.constructionContingency +
      dev.developmentManagementFee +
      dev.otherDdCosts +
      dev.tenantRelocationCosts) /
    leaseYears;
  const approval = dev.developerApprovalShare / leaseYears;

  const components: RentBuildUpComponent[] = [
    {
      key: "land_recovery",
      label: "Land acquisition recovery",
      annualAmount: land,
    },
    {
      key: "construction_debt_service",
      label: "Construction debt + holding interest",
      annualAmount: debt,
    },
    {
      key: "soft_costs",
      label: "Soft costs (consultants, statutory, mgmt fee)",
      annualAmount: soft,
    },
    {
      key: "approval_recovery",
      label: "Approval cost recovery (developer share)",
      annualAmount: approval,
    },
    {
      key: "opex_recovery",
      label: "Outgoings / opex pass-through",
      annualAmount: opexY1,
    },
    {
      key: "equity_return",
      label: "Developer equity return @ hurdle IRR",
      annualAmount: equity,
    },
  ];
  // The operator contribution does not reduce developer equity. It can lower
  // the floor only through the reduced construction debt costs above.
  const costRecoveryRent = components.reduce((s, c) => s + c.annualAmount, 0);
  const decomp = computeRentDecomposition(scenario);
  return {
    components,
    costRecoveryRent,
    scenarioRentY1,
    marginOverreach: scenarioRentY1 - costRecoveryRent,
    calibratedRentY1: decomp.calibratedRentY1,
    calibratedSolverConverged: decomp.calibratedSolverConverged,
  };
}

/** Stress the active cost basis on a cloned trial, preserving source inputs and margin rates. */
function applyTpcCostStress(scenario: Scenario, scale: number): void {
  const basis = scenario.assumptions.developerCostBasis;
  if (basis?.mode === "loaded_aggregate") {
    scenario.assumptions.developerCostBasis = {
      ...basis,
      buildingWorks: basis.buildingWorks * scale,
      siteWorks: basis.siteWorks * scale,
      upperBoundBuildingWorks: basis.upperBoundBuildingWorks * scale,
      upperBoundSiteWorks: basis.upperBoundSiteWorks * scale,
      upperBoundTotal: basis.upperBoundTotal * scale,
    };
  } else {
    for (const building of scenario.buildings) {
      if (Number.isFinite(building.constructionPerSqmGfa))
        building.constructionPerSqmGfa *= scale;
    }
  }
}

function activeHardWorks(scenario: Scenario): number {
  const basis = scenario.assumptions.developerCostBasis;
  if (basis?.mode === "loaded_aggregate")
    return basis.buildingWorks + basis.siteWorks;
  return scenario.buildings.reduce(
    (sum, building) => sum + building.gfaSqm * building.constructionPerSqmGfa,
    0,
  );
}

export function rentMultiplier(
  scenario: Scenario,
  metric:
    | "developerGearedIrr"
    | "developerGearedEarlyExitIrr"
    | "developerGearedWholeOfLifeIrr"
    | "developerYieldOnCost"
    | "developerMarginOnCost" = "developerGearedIrr",
  deltaTpc = 1_000_000,
): RentMultiplierResult {
  // Reads only .years/.developer + a core metric; use computeCore to skip the solvers (perf).
  const baseline = computeCoreRaw(scenario);
  const baselineRentY1 = baseline.years[0]?.rentGross ?? 0;
  const baselineTpc = baseline.developer.totalCost;
  const targetMetricValue = baseline.kpis[metric] as number;
  const baselineHard = activeHardWorks(scenario);
  if (baselineHard === 0) {
    return {
      baselineTpc,
      baselineRentY1,
      deltaTpc,
      bumpedRentY1: baselineRentY1,
      perDollarTpc: 0,
      lifetimeRentDelta: 0,
      lifetimeRentDeltaNpv: 0,
      targetMetric: metric,
    };
  }
  const constructionScale = (baselineHard + deltaTpc) / baselineHard;
  const bumped = clone(scenario);
  applyTpcCostStress(bumped, constructionScale);
  const solved = solveRentForHurdle(bumped, metric, targetMetricValue);
  if (!solved.converged) {
    return {
      baselineTpc,
      baselineRentY1,
      deltaTpc,
      bumpedRentY1: baselineRentY1,
      perDollarTpc: Number.NaN,
      lifetimeRentDelta: Number.NaN,
      lifetimeRentDeltaNpv: Number.NaN,
      targetMetric: metric,
    };
  }
  const scaledScenario = clone(bumped);
  for (const b of scaledScenario.buildings) {
    if (Number.isFinite(b.netRentPerSqm)) b.netRentPerSqm *= solved.rentScale;
  }
  const bumpedResult = computeCoreRaw(scaledScenario); // reads only .years[0].rentGross + .developer.totalCost (raw core)
  const bumpedRentY1 = bumpedResult.years[0]?.rentGross ?? 0;
  // P5-R3 (root #4): divide the rent uplift by the REALISED TPC delta, not the nominal $1M hard bump.
  // The $1M of hard construction grosses up to a larger total-project-cost delta via the %-of-hard soft
  // costs (consultants, contingency, prelims, margin, holding, DM fee), so "per $1M TPC" must use the
  // actual TPC increase — otherwise it overstates the rate ~46%.
  const realisedTpcDelta = bumpedResult.developer.totalCost - baselineTpc;
  const perDollarTpc =
    realisedTpcDelta !== 0
      ? (bumpedRentY1 - baselineRentY1) / realisedTpcDelta
      : Number.NaN;
  const a = scenario.assumptions;
  // Lifetime figures normalised to the per-$1M-realised-TPC display basis (perDollarTpc × deltaTpc).
  const perMillionRentDelta = perDollarTpc * deltaTpc;
  const lifetimeRentDelta = perMillionRentDelta * a.leaseTermYears;
  const g = effectiveRentGrowth(a);
  const r = Number.isFinite(a.discountRate) ? a.discountRate : 0;
  let lifetimeNpv = 0;
  for (let y = 1; y <= a.leaseTermYears; y++) {
    lifetimeNpv +=
      (perMillionRentDelta * Math.pow(1 + g, y - 1)) / Math.pow(1 + r, y);
  }
  return {
    baselineTpc,
    baselineRentY1,
    deltaTpc,
    bumpedRentY1,
    perDollarTpc,
    lifetimeRentDelta,
    lifetimeRentDeltaNpv: lifetimeNpv,
    targetMetric: metric,
  };
}

export function tpcSweep(
  scenario: Scenario,
  opts: { minPct: number; maxPct: number; steps: number } = {
    minPct: 0.85,
    maxPct: 1.55,
    steps: 28,
  },
): TpcSweepResult {
  // Reads only .developer/core KPIs; use computeCore to skip the solvers (perf).
  const baseline = computeCore(scenario);
  const baselineTpc = baseline.developer.totalCost;
  const baselineIrr = baseline.kpis.developerPrimaryGearedIrr;
  // This sensitivity changes project cost but retains the base scenario's financing-reference
  // contract. Derive that reference once; never pay a nested calibration for every TPC row.
  const hurdleIrr = baseline.developer.gearedHurdle;
  // In loaded_aggregate mode the per-m² rates are dead; stress lands on the
  // loaded building/site works instead (see applyTpcCostStress).
  const baselineHard = activeHardWorks(scenario);

  const rows: TpcSweepRow[] = [];
  const stepSize = (opts.maxPct - opts.minPct) / Math.max(1, opts.steps - 1);
  for (let i = 0; i < opts.steps; i++) {
    const pct = opts.minPct + i * stepSize;
    const trial = clone(scenario);
    if (baselineHard > 0) {
      applyTpcCostStress(trial, (baselineHard * pct) / baselineHard);
    }
    // Hot loop (opts.steps iterations): reads only .developer/core KPIs → computeCore (perf).
    const r = computeCoreRaw(trial);
    rows.push({
      tpc: r.developer.totalCost,
      tpcPctOfBase: baselineTpc > 0 ? r.developer.totalCost / baselineTpc : 1,
      developerIrr: r.kpis.developerPrimaryIrr,
      // This field is the sweep's primary geared series; follow the selected exit horizon.
      developerGearedIrr: r.kpis.developerPrimaryGearedIrr,
      developerGearedWholeOfLifeIrr: r.kpis.developerGearedWholeOfLifeIrr,
      developerMarginOnCost: r.kpis.developerMarginOnCost,
      developerYieldOnCost: r.kpis.developerYieldOnCost,
      hurdleMet:
        Number.isFinite(r.kpis.developerPrimaryGearedIrr) &&
        Number.isFinite(hurdleIrr)
          ? r.kpis.developerPrimaryGearedIrr >= hurdleIrr
          : null,
      hurdleIrr,
    });
  }
  let cliffTpc: number | null = null;
  for (let i = 1; i < rows.length; i++) {
    if (rows[i - 1].hurdleMet === true && rows[i].hurdleMet === false) {
      const rowA = rows[i - 1],
        rowB = rows[i];
      const denom = rowA.developerGearedIrr - rowB.developerGearedIrr;
      if (
        Number.isFinite(rowA.developerGearedIrr) &&
        Number.isFinite(rowB.developerGearedIrr) &&
        Math.abs(denom) > 1e-9
      ) {
        // Interpolate the geared series against the single baseline reference without per-row solves.
        const cliffHurdleIrr = (rowA.hurdleIrr + rowB.hurdleIrr) / 2;
        const frac = Math.max(
          0,
          Math.min(1, (rowA.developerGearedIrr - cliffHurdleIrr) / denom),
        );
        cliffTpc = rowA.tpc + (rowB.tpc - rowA.tpc) * frac;
      }
      break;
    }
  }
  return {
    baselineTpc,
    baselineIrr,
    hurdleIrr,
    cliffTpc,
    cliffPctOverBase:
      cliffTpc !== null && baselineTpc > 0
        ? (cliffTpc - baselineTpc) / baselineTpc
        : null,
    rows,
  };
}

// ---------- Monte Carlo ----------
export interface MonteCarloVariable {
  key: string;
  label: string;
  range: number; // +/- fraction, e.g. 0.15 = ±15%
  apply: (s: Scenario, factor: number) => void;
}

export interface MonteCarloResult {
  iterations: number;
  developerGearedIrr: {
    mean: number;
    p5: number;
    p25: number;
    p50: number;
    p75: number;
    p95: number;
    histogram: number[];
    bins: number[];
  };
  reddamNpv: {
    mean: number;
    p5: number;
    p25: number;
    p50: number;
    p75: number;
    p95: number;
    histogram: number[];
    bins: number[];
  };
  hurdleMetPct: number; // % of iterations where matched geared IRR >= the base-scenario derived reference; NaN when unavailable
  hurdleReferenceIrr?: number; // cashflow-equivalent geared reference derived once from the base scenario
  // Operator covenant breach probabilities — operator-side risk read (symmetric to hurdleMetPct)
  breach: {
    rentAboveCautionPct: number; // P(rentPctRevenueYear1 > shared occupancy-cost caution band)
    rentAboveThresholdPct: number; // P(rentPctRevenueYear1 > shared hard ceiling)
    ebitdaNegativeY1Pct: number; // P(yearOneEbitda < 0 — Y1 operator cash loss)
    ebitdaNegativePostRampPct: number; // P(years[rampYears].reddamEbitda < 0 — first post-ramp year check)
    dscrBreachPct: number; // P(dscr < 1.25 when levered; always 0 when dscrStatus === 'na')
  };
}

/** W3 — Lease-vs-Own illustrative NPV comparison.
 *
 * Discounts the full rent outflow stream at the school's WACC and compares
 * it to a simplified "own" path: pay capex at Y0, recover terminal value at
 * end of lease term.  delta > 0 means owning appears cheaper at schoolWacc.
 *
 * All outputs are ILLUSTRATIVE — see mandatory caveats in the returned object.
 */
export function computeLeaseVsOwn(
  scenario: Scenario,
  result: ModelResult,
): LeaseVsOwnResult {
  const a = scenario.assumptions;
  // Guard: -1 passes isFinite but causes (1+w)^n = 0 → division by zero. Require w > -1.
  const w =
    Number.isFinite(a.schoolWacc) && a.schoolWacc > -1 ? a.schoolWacc : 0.085;

  // PV of total occupancy-cost outflows at schoolWacc (negative — cash out).
  // Includes building rent AND amenity rent (sports field + parking LL rent) so the
  // lease path reflects the full cost the school pays.
  const leasePvAtWacc = result.years.reduce(
    (acc, y, i) => acc - (y.rentGross + y.amenityRent) / Math.pow(1 + w, i + 1),
    0,
  );

  const capex = result.developer.totalCost;
  // Use netRealisation (after selling costs) — school as seller incurs same agent fee.
  // Use result.years.length (modelYears) not leaseTermYears to keep both paths on the
  // same time horizon; if they diverge the leasePvAtWacc already stops at modelYears.
  const terminal = result.developer.netRealisation;
  const horizonYears = result.years.length;
  const terminalPv = terminal / Math.pow(1 + w, horizonYears);
  const ownPathNpv = -capex + terminalPv;
  const delta = ownPathNpv - leasePvAtWacc;
  // Break-even capex: delta = 0 when ownPathNpv = leasePvAtWacc
  // → -capex + terminalPv = leasePvAtWacc → capex = terminalPv - leasePvAtWacc
  // leasePvAtWacc < 0, so -leasePvAtWacc > 0, making breakEvenCapex > terminalPv.
  const impliedBreakEvenCapex = terminalPv - leasePvAtWacc;

  return {
    leasePvAtWacc,
    ownPathCapex: capex,
    ownPathTerminalValue: terminal,
    ownPathTerminalValuePv: terminalPv,
    ownPathNpv,
    deltaOwningVsLeasing: delta,
    impliedBreakEvenCapex,
    schoolWacc: w,
    caveats: [
      "Own path assumes 100% of capex raised at schoolWacc — ignores capital rationing and borrowing limits.",
      "Terminal value uses the developer's exit cap rate; education-sector asset liquidity may yield a different market value.",
      "Own path excludes ongoing ownership costs: council rates, insurance, lifecycle capex, and principal maintenance.",
      "Construction risk and timeline uncertainty remain with the school in the own path.",
      "A positive delta (owning appears cheaper) does not constitute a recommendation to build — risk tolerance, capital availability, and off-balance-sheet treatment are school-specific.",
    ],
  };
}

// Monte Carlo functions extracted to ./solvers/monte-carlo; thin delegations here preserve
// the existing public API (Model.monteCarlo / Model.monteCarloChunked).
export function monteCarlo(
  scenario: Scenario,
  variables: MonteCarloVariable[],
  iterations = 1000,
): MonteCarloResult {
  const baseline = computeCore(scenario);
  const baseScenarioGearedHurdle = baseline.developer.gearedHurdleConverged
    ? baseline.developer.gearedHurdle
    : Number.NaN;
  return _monteCarlo(
    scenario,
    variables,
    computeCoreRaw,
    iterations,
    baseScenarioGearedHurdle,
  );
}

export async function monteCarloChunked(
  scenario: Scenario,
  variables: MonteCarloVariable[],
  iterations = 1000,
  onProgress?: (completed: number, total: number) => void,
): Promise<MonteCarloResult> {
  const baseline = computeCore(scenario);
  const baseScenarioGearedHurdle = baseline.developer.gearedHurdleConverged
    ? baseline.developer.gearedHurdle
    : Number.NaN;
  return _monteCarloChunked(
    scenario,
    variables,
    computeCoreRaw,
    iterations,
    onProgress,
    baseScenarioGearedHurdle,
  );
}

// ---------- default scenarios ----------
export function baseScenario(): Scenario {
  const scenario: Scenario = {
    id: "base",
    name: "Base Case",
    description:
      "Realistic mid-line assumptions for a metropolitan campus lease.",
    assumptions: {
      projectName: "Julius Avenue Campus Lease",
      operatorName: "Reddam House",
      campusName: "Julius Avenue Campus",
      address: "10–12 Julius Avenue, North Ryde NSW",
      leaseTermYears: 25,
      modelYears: 25,
      purchasePrice: 68_000_000,
      fullSitePrice: 113_000_000,
      campusParcelAllocationLow: 113_000_000,
      campusParcelAllocationHigh: 113_000_000,
      stampDutyRate: 0.055,
      acquisitionFeeRate: 0.01,
      consultantCostRate: 0.04,
      statutoryCostRate: 0.05,
      planningApprovalCosts: 1_250_000,
      developerContingencyRate: 0.05,
      agentsFeeRate: 0.05,
      devManagementFeeRate: 0.04,
      otherDdCosts: 300_000,
      tenantRelocationCosts: 2_000_000,
      developmentMonths: 24,
      holdingInterestRate: 0.055,
      developerHurdleIRR: 0.12,
      capRate: 0.055,
      reddamFitoutAllowance: 4_000_000,
      fitoutAmortisationYears: 10,
      reddamCapexContribution: 0,

      rentIndexationPct: 0.035,
      feePerStudentYear1: 38_000,
      earlyLearningFeeYear1: 28_000,
      primaryFeeYear1: 34_000,
      secondaryFeeYear1: 42_000,
      seniorFeeYear1: 45_000,
      earlyLearningShare: 0.12,
      primaryShare: 0.48,
      secondaryShare: 0.28,
      seniorShare: 0.12,
      tuitionGrowthPct: 0.04,
      foundingFamilyDiscount: 0,
      siblingDiscount: 0,
      discountDecayYears: 5,
      rampYears: 5,
      startingUtilisation: 0.75,
      steadyStateUtilisation: 0.92,
      targetEnrolment: 0,
      enrolmentCaptureRate: 0.15, // share of catchment school-age children captured (catchment pool estimate)
      variableCostPerStudent: 11_500,
      fixedOpex: 4_200_000,
      fixedOpexGrowth: 0.03,
      // 44-58 Waterloo Rd actuals: sports field 6,250 m²; car park 51 bays total (operator takes all).
      sportsFieldAreaSqm: 6_250,
      sportsFieldIncomePerSqm: 185,
      // Amenity LL rent (operator pays developer → NOI): user-set developer ask. Amenity construction
      // (developer build cost → TDC): FLAG construction-cre-panel — placeholders, NOT market assertions.
      sportsFieldRentPerSqm: 350,
      sportsFieldConstructionPerSqm: 120,
      parkingBays: 51,
      parkingIncomePerBay: 0,
      parkingRentPerBay: 3_000,
      parkingConstructionCostPerBay: 6_000,
      // Covered play area: annual lump-sum ground rent (operator → NOI) and
      // build cost (→ TDC). No operator hire income.
      coveredPlayAreaGroundRent: 0,
      coveredPlayAreaConstructionCost: 0,
      ancillaryGrowthPct: 0.035, // operator hire income escalates with CPI (mirrors rentIndexationPct) — P4-R5/#13

      // Casual/event parking sub-allocations zeroed — the prior 95/150/70 figures were leftovers from the
      // wrong 320-bay total (they exceed 51). Operator occupies all 51 bays; set these + parkingIncomePerBay
      // if the school casually sublets/charges for any bays.
      weekdayParentParkingBays: 0,
      weekdayParentParkingFee: 8,
      weekdayParentParkingDays: 190,
      weekendSportsParkingBays: 0,
      weekendSportsParkingFee: 12,
      weekendSportsParkingDays: 42,
      staffReservedParkingBays: 0,
      discountRate: 0.085,
      corporateTaxRate: 0.3,
      minimumCashReserveMonths: 3,
      // debt model defaults (sourced from 40 Edward St workbook)
      constructionLvrRate: 0.7,
      constructionMarginRate: 0.017,
      constructionLineFeeRate: 0.017,
      termLoanLvrRate: 0.6,
      termLoanMarginRate: 0.016,
      termLoanHoldYears: 5,
      useScurveDrawdown: true,
      preliminariesRate: 0.145, // builder preliminaries as % of hard (ACT BGA ~14.5%)
      builderMarginRate: 0.05, // builder margin/overhead as % of hard
      schoolSpecTier: "private_mid", // Reddam = private mid-spec; drives the construction $/m² benchmark tier
      performanceBondPct: 0.1,
      defectsLiabilityMonths: 24,
      bankGuaranteeMonths: 6, // months of gross rent; 6–12 common for special-purpose BTS (tenant typically negotiates down)
      externalWorksPct: 0.15,
      equityReturnFormulation: "perpetuity",
      // DM fee base: development cost only — land + all financing EXCLUDED by default (conservative for tenant).
      dmFeeIncludeLand: false,
      dmFeeIncludeHoldingInterest: false,
      dmFeeIncludeLineFee: false,
      dmFeeIncludeConstructionInterest: false,
      rentEscalationMethod: "fixed",
      cpiAssumption: DEFAULT_CPI_ASSUMPTION,
      marketReviewPct: 0.04,
      developerExitYear: 25,
      ssdaCost: 500_000,
      changeOfUseCost: 150_000,
      otherApprovalCosts: 250_000,
      approvalCostReddamSharePct: 0.5,
      siteDealAllocationStatus: "unverified",
      // W2 lease incentive (defaults to no-op)
      leaseIncentiveLumpSum: 0,
      rentFreeMonths: 0,
      incentiveAmortisationYears: 25, // = leaseTermYears default
      // W3 lease-vs-own (RBA cash rate + ~200 bps education spread)
      schoolWacc: 0.085,
    },
    buildings: [
      {
        id: "b1",
        name: "10 Julius Avenue",
        nlaSqm: 5662,
        gfaSqm: 9722,
        constructionPerSqmGfa: 3600,
        reddamCapex: 3_500_000,
        netRentPerSqm: 650,
        outgoingsPerSqm: 120,
        enrolmentCapacity: 240,
        comesOnlineYear: 1,
      },
      {
        id: "b2",
        name: "10A Julius Avenue",
        nlaSqm: 2742,
        gfaSqm: 2742,
        constructionPerSqmGfa: 3600,
        reddamCapex: 1_000_000,
        netRentPerSqm: 650,
        outgoingsPerSqm: 120,
        enrolmentCapacity: 120,
        comesOnlineYear: 1,
      },
      {
        id: "b3",
        name: "12A Julius Avenue",
        nlaSqm: 4371,
        gfaSqm: 5828,
        constructionPerSqmGfa: 3600,
        reddamCapex: 2_100_000,
        netRentPerSqm: 650,
        outgoingsPerSqm: 120,
        enrolmentCapacity: 180,
        comesOnlineYear: 1,
      },
      {
        id: "b4",
        name: "12B Julius Avenue",
        nlaSqm: 4092,
        gfaSqm: 5456,
        constructionPerSqmGfa: 3600,
        reddamCapex: 2_000_000,
        netRentPerSqm: 650,
        outgoingsPerSqm: 120,
        enrolmentCapacity: 160,
        comesOnlineYear: 1,
      },
    ],
    builderQuotes: JULIUS_BUILDER_QUOTES_SEED,
    actionItems: [],
    auditLog: [],
  };
  scenario.actionItems = generateActionItems(scenario);
  return scenario;
}

/** Per-scenario "lever" assumption keys — the inputs that DEFINE each scenario's character
 *  (these are exactly the fields `defaultScenarios()` varies off Base). "Push to all scenarios"
 *  copies every OTHER assumption + the buildings from the active scenario into the others, while
 *  leaving these levers untouched, so Downside/Upside/Stretch keep their identity. */
export const SCENARIO_LEVER_KEYS = [
  "rampYears",
  "startingUtilisation",
  "steadyStateUtilisation",
  "tuitionGrowthPct",
  "rentIndexationPct",
  "capRate",
  "feePerStudentYear1",
  "reddamFitoutAllowance",
  "parkingIncomePerBay",
  "catchmentCode", // FOUNDATION: per-scenario market picker target (CENSUS seam)
] as const;

/** Downside construction stress, shared by the legacy per-m² lever and the loaded-works
 *  scaling in productionScenarios() so the two cannot diverge (loop#034 review). */
const DOWNSIDE_CAPEX_SCALE = 1.1;

export function defaultScenarios(): Scenario[] {
  const base = baseScenario();

  const down = clone(base);
  down.id = "down";
  down.name = "Downside";
  down.description = "Slow ramp, weak fee growth, higher capex.";
  down.assumptions.rampYears = 7;
  down.assumptions.startingUtilisation = 0.55;
  down.assumptions.steadyStateUtilisation = 0.78;
  down.assumptions.tuitionGrowthPct = 0.025;
  down.assumptions.rentIndexationPct = 0.04;
  down.buildings = down.buildings.map((b) => ({
    ...b,
    constructionPerSqmGfa: b.constructionPerSqmGfa * DOWNSIDE_CAPEX_SCALE,
  }));

  const up = clone(base);
  up.id = "up";
  up.name = "Upside";
  up.description = "Faster ramp, strong fee growth, parking income realised.";
  up.assumptions.rampYears = 3;
  up.assumptions.steadyStateUtilisation = 0.97;
  up.assumptions.tuitionGrowthPct = 0.05;
  up.assumptions.parkingIncomePerBay = 1800;

  const stretch = clone(base);
  stretch.id = "stretch";
  stretch.name = "Stretch";
  stretch.description = "Lean operator capex, tighter cap rate, premium fees.";
  stretch.assumptions.reddamFitoutAllowance = 2_500_000;
  stretch.assumptions.capRate = 0.05;
  stretch.assumptions.feePerStudentYear1 = 42_000;
  stretch.assumptions.tuitionGrowthPct = 0.045;

  return [base, down, up, stretch];
}

/**
 * The scenario set the SPA seeds for a fresh workspace (loop#034, re-seeded on
 * the Waterloo Road deal in loop#037 — Kyle, 2026-09-08: "seed on waterloo").
 *
 * Base is the Waterloo working base case (`src/data/waterloo-working-base-case.json`,
 * the 8 September 2026 export with its plan evidence, builder quote and action
 * items) with the Versatile RevG loaded cost basis at the stated 5% margin applied
 * at runtime,
 * so the preset constants stay the single source of the cost numbers. The three
 * variants are RELATIVE to that base (the legacy `defaultScenarios()` variants are
 * absolute Julius Avenue lever values and do not transfer: Waterloo starts at 35%
 * utilisation and ramps to 100%, so "Downside 55% → 78%" would be an upside).
 *
 * `baseScenario()` / `defaultScenarios()` stay the legacy net-trade engine anchor
 * that the pinned test corpus reads; only the product entry point moves.
 */
export const PRODUCTION_VARIANT_RULES = {
  down: {
    // Rent-indexation DELTA is +0.5%: higher rent indexation stresses operator
    // affordability but benefits the landlord — intentionally operator-first.
    rampYearsDelta: 2,
    startingUtilisationScale: 0.8,
    steadyStateUtilisationScale: 0.9,
    tuitionGrowthDelta: -0.01,
    rentIndexationDelta: 0.005,
    capexScale: DOWNSIDE_CAPEX_SCALE,
  },
  up: {
    rampYearsDelta: -2,
    // Guard only: inert on the current 100% utilisation base; clamps nothing today.
    steadyStateUtilisationFloor: 0.97,
    tuitionGrowthDelta: 0.01,
    parkingIncomePerBayFloor: 1800,
  },
  stretch: {
    capRateDelta: -0.005,
    feePerStudentScale: 1.1,
    tuitionGrowthDelta: 0.005,
  },
} as const;

export function productionScenarios(): Scenario[] {
  const base = applyVersatileRevGCostPreset(
    normalizeScenario(
      structuredClone(WATERLOO_WORKING_BASE_CASE as unknown as Scenario),
    ),
  );
  base.id = "base";
  base.name = "Base Case";

  const down = clone(base);
  down.id = "down";
  down.name = "Downside";
  down.description =
    "Slow ramp, weaker fee growth, higher rent indexation, loaded works +10%.";
  down.assumptions.rampYears += PRODUCTION_VARIANT_RULES.down.rampYearsDelta;
  down.assumptions.startingUtilisation *=
    PRODUCTION_VARIANT_RULES.down.startingUtilisationScale;
  down.assumptions.steadyStateUtilisation *=
    PRODUCTION_VARIANT_RULES.down.steadyStateUtilisationScale;
  down.assumptions.tuitionGrowthPct +=
    PRODUCTION_VARIANT_RULES.down.tuitionGrowthDelta;
  down.assumptions.rentIndexationPct +=
    PRODUCTION_VARIANT_RULES.down.rentIndexationDelta;
  {
    // In loaded mode the per-m² lever is dead (loaded works are a fixed
    // aggregate), so Downside scales the loaded works and their upper bound.
    const basis = down.assumptions.developerCostBasis!;
    const scale = PRODUCTION_VARIANT_RULES.down.capexScale;
    down.assumptions.developerCostBasis = {
      ...basis,
      buildingWorks: basis.buildingWorks * scale,
      siteWorks: basis.siteWorks * scale,
      upperBoundBuildingWorks: basis.upperBoundBuildingWorks * scale,
      upperBoundSiteWorks: basis.upperBoundSiteWorks * scale,
      upperBoundTotal: basis.upperBoundTotal * scale,
      sourceName: `${basis.sourceName} — Downside stress × ${scale}`,
    };
  }

  const up = clone(base);
  up.id = "up";
  up.name = "Upside";
  up.description = "Faster ramp, stronger fee growth, parking income realised.";
  up.assumptions.rampYears = Math.max(
    1,
    up.assumptions.rampYears + PRODUCTION_VARIANT_RULES.up.rampYearsDelta,
  );
  up.assumptions.steadyStateUtilisation = Math.max(
    up.assumptions.steadyStateUtilisation,
    PRODUCTION_VARIANT_RULES.up.steadyStateUtilisationFloor,
  );
  up.assumptions.tuitionGrowthPct +=
    PRODUCTION_VARIANT_RULES.up.tuitionGrowthDelta;
  up.assumptions.parkingIncomePerBay = Math.max(
    up.assumptions.parkingIncomePerBay,
    PRODUCTION_VARIANT_RULES.up.parkingIncomePerBayFloor,
  );

  const stretch = clone(base);
  stretch.id = "stretch";
  stretch.name = "Stretch";
  stretch.description = "Tighter cap rate, premium fees, stronger fee growth.";
  stretch.assumptions.capRate += PRODUCTION_VARIANT_RULES.stretch.capRateDelta;
  stretch.assumptions.feePerStudentYear1 *=
    PRODUCTION_VARIANT_RULES.stretch.feePerStudentScale;
  stretch.assumptions.tuitionGrowthPct +=
    PRODUCTION_VARIANT_RULES.stretch.tuitionGrowthDelta;

  return [base, down, up, stretch];
}

/**
 * Migrate a loaded / imported / persisted scenario to the current assumption schema by merging
 * `baseScenario()` defaults UNDERNEATH the scenario's own values. A scenario saved or shared before
 * a newer assumption field existed (e.g. `preliminariesRate`, `builderMarginRate`, `schoolSpecTier`,
 * `bankGuaranteeMonths`) would otherwise leave those fields `undefined` and compute `NaN`. The
 * scenario's own values always win; defaults only fill gaps.
 */
export function normalizeScenario(s: Scenario): Scenario {
  const defaults = baseScenario().assumptions;
  return {
    ...s,
    assumptions: { ...defaults, ...s.assumptions },
  };
}

// ---------- model checks ----------
const closeTo = (a: number, b: number): boolean => {
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  const diff = Math.abs(a - b);
  const scale = Math.max(Math.abs(a), Math.abs(b), 1);
  return diff <= scale * 1e-9;
};

export function modelChecks(
  scenario: Scenario,
  result?: ModelResult,
): CheckResult[] {
  if (!scenario?.assumptions || !Array.isArray(scenario.buildings)) return [];
  result ??= compute(scenario);
  if (!result?.developer || !result.years || !result.campus) return [];
  const a = scenario.assumptions;
  const d = result.developer;
  const checks: CheckResult[] = [];

  // P9-R1 (#meta): RAW-input cost recalc. Recompute the multiplicative cost components straight from the
  // assumptions/buildings (independent of the developer object) so a computation error in any of them
  // ACTUALLY fails the check — the old version summed d.* fields from the same source as d.totalCost and
  // so could only catch a corrupted total, not a wrong calc. (Holding interest + DM fee are kept as model
  // outputs — too path-dependent to re-derive here; the leverage-neutrality + noi-cost-backed checks below
  // exercise the harder paths.)
  const rawLoadedBasis =
    a.developerCostBasis?.mode === "loaded_aggregate"
      ? a.developerCostBasis
      : undefined;
  const rawHard = rawLoadedBasis
    ? rawLoadedBasis.buildingWorks
    : scenario.buildings.reduce(
        (s, b) =>
          s +
          (Number.isFinite(b.gfaSqm) && Number.isFinite(b.constructionPerSqmGfa)
            ? b.gfaSqm * b.constructionPerSqmGfa
            : 0),
        0,
      );
  const rawSiteWorks = rawLoadedBasis ? rawLoadedBasis.siteWorks : 0;
  const rawAmenity = rawLoadedBasis
    ? 0
    : a.sportsFieldAreaSqm * a.sportsFieldConstructionPerSqm +
      a.parkingBays * a.parkingConstructionCostPerBay +
      a.coveredPlayAreaConstructionCost;
  const rawAllowanceBase = rawLoadedBasis ? rawHard + rawSiteWorks : rawHard;
  const rawConsultants = resolveCostAllowance(
    a.developerCostAllowances?.professionalFees,
    a.consultantCostRate,
    rawAllowanceBase,
  ).amount;
  const rawContingency = resolveCostAllowance(
    a.developerCostAllowances?.contingency,
    a.developerContingencyRate,
    rawAllowanceBase,
  ).amount;
  const rawStatutory =
    rawLoadedBasis && Number.isFinite(a.developerCostAllowances?.consentsAmount)
      ? Math.max(0, a.developerCostAllowances?.consentsAmount ?? 0)
      : rawHard * a.statutoryCostRate;
  const rawComponents: [string, number, number][] = [
    ["stamp duty", a.purchasePrice * a.stampDutyRate, d.stampDuty],
    [
      "acquisition fee",
      a.purchasePrice * a.acquisitionFeeRate,
      d.acquisitionFee,
    ],
    ["hard construction", rawHard, d.hardConstruction],
    ["site works", rawSiteWorks, d.siteWorksConstruction],
    [
      "preliminaries",
      rawLoadedBasis ? 0 : rawHard * a.preliminariesRate,
      d.preliminaries,
    ],
    [
      "builder margin",
      rawLoadedBasis ? 0 : rawHard * a.builderMarginRate,
      d.builderMargin,
    ],
    ["consultants", rawConsultants, d.consultantCosts],
    ["statutory", rawStatutory, d.statutoryCosts],
    ["contingency", rawContingency, d.constructionContingency],
    ["amenity construction", rawAmenity, d.amenityConstruction],
  ];
  const compMismatches = rawComponents
    .filter(([, raw, got]) => !closeTo(raw, got))
    .map(([name]) => name);
  const expectedCost =
    d.purchasePrice +
    d.stampDuty +
    d.acquisitionFee +
    d.hardConstruction +
    d.preliminaries +
    d.builderMargin +
    d.consultantCosts +
    d.statutoryCosts +
    d.planningApprovalCosts +
    d.developerApprovalShare +
    d.constructionContingency +
    d.holdingInterest +
    d.otherDdCosts +
    d.tenantRelocationCosts +
    d.siteWorksConstruction +
    d.amenityConstruction +
    d.developmentManagementFee;
  checks.push({
    id: "cost-recalc",
    area: "Cost model",
    level:
      compMismatches.length === 0 && closeTo(expectedCost, d.totalCost)
        ? "pass"
        : "fail",
    label: "Total developer cost reconciles (raw-input recalc)",
    // loop#004: a fail can hinge on a sub-dollar gap the compact amounts render identically —
    // name the gap in cents on the fail path (pass keeps the existing compact detail).
    detail: compMismatches.length
      ? `Component mismatch vs raw inputs: ${compMismatches.join(", ")}.${
          // loop#005: when the stack total ALSO drifts from totalCost, the mismatch list
          // alone hid how far the total is off — name both facts.
          closeTo(expectedCost, d.totalCost)
            ? ""
            : ` Discrepancy (model total − component stack) ${preciseCurrency(d.totalCost - expectedCost)}.`
        }`
      : `Raw components tie; stack = ${compactCurrency(expectedCost)}; model = ${compactCurrency(d.totalCost)}.${
          closeTo(expectedCost, d.totalCost)
            ? ""
            : ` Discrepancy (model total − component stack) ${preciseCurrency(d.totalCost - expectedCost)}.`
        }`,
  });

  checks.push({
    id: "funding-sources-uses",
    area: "Debt",
    level: Math.abs(d.fundingResidual) <= 0.01 ? "pass" : "fail",
    label: "Developer funding sources equal pre-finance uses",
    // loop#003: this check FAILS at |residual| > $0.01, so its evidence must keep cents —
    // compactCurrency rounded sub-dollar residuals to "$0", inviting dismissal of a real fail.
    detail: `Equity + actual debt + operator contribution differs from pre-finance TDC by ${preciseCurrency(d.fundingResidual)}.`,
  });

  // P9-R1 (#meta): return-metric sanity bounds — flag absurd finite values AND metrics that could not be
  // computed (NaN/±∞ in a degenerate scenario, e.g. zero equity or zero realisation). A non-computable
  // return must never read PASS; it reads REVIEW here and FAIL in the `finite` check below. Warns, doesn't fail.
  const boundIssues: string[] = [];
  const inRange = (name: string, v: number, lo: number, hi: number) => {
    if (!Number.isFinite(v)) boundIssues.push(`${name} not computable (${v})`);
    else if (v < lo || v > hi)
      boundIssues.push(`${name}=${v.toFixed(2)} ∉ [${lo},${hi}]`);
  };
  inRange("ungeared IRR", d.irr, -1, 5);
  inRange("geared dev-exit IRR", d.gearedDevelopmentIrr, -1, 5);
  inRange("geared WoL IRR", d.gearedWholeOfLifeIrr, -1, 5);
  inRange("yield on cost", d.yieldOnCost, -0.5, 1);
  inRange("margin on cost", d.marginOnCost, -1, 5);
  inRange("money multiple", d.moneyMultiple, -5, 100);
  inRange("yield on cash", d.yieldOnCash, -0.5, 5);
  inRange("geared hurdle reference IRR", d.gearedHurdle, -1, 5);
  // Return metrics that exist only under an early-exit strategy are NaN by design otherwise.
  const exitYearSet = (a.developerExitYear ?? a.modelYears) < a.modelYears;
  if (exitYearSet) {
    inRange("ungeared early-exit IRR", d.ungearedEarlyExitIrr, -1, 5);
    inRange("geared early-exit IRR", d.gearedEarlyExitIrr, -1, 5);
  }
  checks.push({
    id: "return-bounds",
    area: "Returns",
    level: boundIssues.length ? "warn" : "pass",
    label: "Developer return metrics are within sane bounds",
    detail: boundIssues.length
      ? boundIssues.join("; ")
      : "IRR / yield / margin / money-multiple all within plausible ranges.",
  });

  // P9-R1 (#meta): every NOI rent component must be backed by ITS OWN cost line — catches costless income
  // (the #17/#18 class, where ancillary capitalised value has no cost/land/opex path). Each amenity is
  // guarded against its own construction input, never the aggregate bucket: a costed sibling must not mask
  // a costless line. Under the loaded-aggregate cost basis the amenities have no per-item split: the
  // scope register maps sports field, parking and covered play to the loaded SITE/OUTDOOR works (never
  // to building works), so only loaded site works back them.
  const noiIssues: string[] = [];
  const loadedBasis = d.costBasisMode === "loaded_aggregate";
  const loadedBacking = loadedBasis && d.siteWorksConstruction > 0;
  if (
    (a.sportsFieldRentPerSqm ?? 0) > 0 &&
    a.sportsFieldAreaSqm > 0 &&
    !(
      loadedBacking ||
      (!loadedBasis &&
        a.sportsFieldAreaSqm * (a.sportsFieldConstructionPerSqm ?? 0) > 0)
    )
  )
    noiIssues.push("sports-field LL rent with no sports-field build cost");
  if (
    (a.parkingRentPerBay ?? 0) > 0 &&
    a.parkingBays > 0 &&
    !(
      loadedBacking ||
      (!loadedBasis &&
        a.parkingBays * (a.parkingConstructionCostPerBay ?? 0) > 0)
    )
  )
    noiIssues.push("parking LL rent with no parking build cost");
  if (
    (a.coveredPlayAreaGroundRent ?? 0) > 0 &&
    !(
      loadedBacking ||
      (!loadedBasis && (a.coveredPlayAreaConstructionCost ?? 0) > 0)
    )
  )
    noiIssues.push(
      "covered play area ground rent with no covered play area build cost",
    );
  if (d.netRentYear1 > 0 && !(d.hardConstruction > 0))
    noiIssues.push("building rent with no construction cost");
  checks.push({
    id: "noi-cost-backed",
    area: "Cost model",
    level: noiIssues.length ? "warn" : "pass",
    label: "Every NOI rent component is backed by a cost line",
    detail: noiIssues.length
      ? noiIssues.join("; ")
      : "Building + amenity rents each carry their construction cost in TDC.",
  });

  // P9-R1 (#meta): leverage neutrality — at zero leverage the geared equity return must equal the ungeared
  // project return for the SAME strategy (sell at PC). The ungeared IRR always books the sale at practical
  // completion, so we neutralise any early-exit-year timing in the zero-leverage sub-compute as well.
  // Zero the operator contribution as well because no debt exists to replace at
  // LVR=0; the contribution-cap validation is a separate invariant.
  if (scenario.buildings.length > 0) {
    const zeroLev = computeCoreRaw({
      ...scenario,
      assumptions: {
        ...a,
        constructionLvrRate: 0,
        termLoanLvrRate: 0,
        reddamCapexContribution: 0,
        developerExitYear: a.modelYears,
      },
    }).developer;
    const levDiff = Math.abs(zeroLev.gearedDevelopmentIrr - zeroLev.irr);
    checks.push({
      id: "leverage-neutrality",
      area: "Returns",
      level: !Number.isFinite(levDiff) || levDiff < 1e-4 ? "pass" : "fail",
      label: "Geared = ungeared at zero leverage",
      detail: `At LVR=0: geared dev-exit ${(zeroLev.gearedDevelopmentIrr * 100).toFixed(2)}% vs ungeared ${(zeroLev.irr * 100).toFixed(2)}% (Δ ${(levDiff * 100).toFixed(4)}pp).`,
    });
  }

  // Construction cost vs market — DETERMINISTIC "is this market?" flag. The benchmark is the
  // sourced market truth; we flag divergence either way. We do NOT raise the benchmark to meet a
  // costing/quote: if the all-in $/m² sits above the market band, the cost is simply not market.
  const allInConstrSqm =
    result.campus.totalGfaSqm > 0
      ? (d.hardConstruction + d.preliminaries + d.builderMargin) /
        result.campus.totalGfaSqm
      : 0;
  const mkt = constructionBenchmark(a.schoolSpecTier);
  const constrNote =
    allInConstrSqm > mkt.high
      ? "ABOVE market band — not market; flag the costing/builder, do not raise the benchmark"
      : allInConstrSqm < mkt.low
        ? "below market band — verify the costing is not optimistically lean"
        : "within market band";
  checks.push({
    id: "construction-vs-market",
    area: "Cost model",
    level:
      allInConstrSqm > mkt.high || allInConstrSqm < mkt.low ? "warn" : "pass",
    label: "All-in construction $/m² vs market benchmark",
    detail: `$${Math.round(allInConstrSqm).toLocaleString("en-AU")}/m² (all-in) vs market $${mkt.low.toLocaleString("en-AU")}–$${mkt.high.toLocaleString("en-AU")} [${a.schoolSpecTier}] — ${constrNote}.`,
  });

  // Reconstruct NOI from RAW inputs (campus building net rent + amenity LL rents recomputed from
  // area×rate / bays×rate) — independent of the developer object, so it is not tautological against
  // d.noi's own composition. Operator hire/sublet income is deliberately NOT here (P4-R3/#18).
  const expectedNoi =
    result.campus.totalYear1NetRent +
    a.sportsFieldAreaSqm * a.sportsFieldRentPerSqm +
    a.parkingBays * a.parkingRentPerBay +
    a.coveredPlayAreaGroundRent;
  checks.push({
    id: "noi-recalc",
    area: "Cost model",
    level: closeTo(expectedNoi, d.noi) ? "pass" : "fail",
    label: "NOI reconciles to building net rent + amenity LL rent",
    detail: `Net rent + amenity LL rent = ${compactCurrency(expectedNoi)}; model = ${compactCurrency(d.noi)}.${
      closeTo(expectedNoi, d.noi)
        ? ""
        : ` Discrepancy (model − recalc) ${preciseCurrency(d.noi - expectedNoi)}.`
    }`,
  });

  checks.push({
    id: "profit-recalc",
    area: "Cost model",
    level: closeTo(d.netRealisation - d.totalCost, d.profit) ? "pass" : "fail",
    label: "Profit = net realisation − total cost",
    detail: `Net realisation ${compactCurrency(d.netRealisation)} less cost ${compactCurrency(d.totalCost)}.${
      closeTo(d.netRealisation - d.totalCost, d.profit)
        ? ""
        : ` Discrepancy (model − recalc) ${preciseCurrency(d.profit - (d.netRealisation - d.totalCost))}.`
    }`,
  });

  const naNs: string[] = [];
  result.years.forEach((y) => {
    Object.entries(y).forEach(([k, v]) => {
      if (typeof v === "number" && !Number.isFinite(v))
        naNs.push(`Y${y.year}.${k}`);
    });
  });
  // Developer and KPI DOLLAR / COUNT outputs — scalars and the exported cashflow vectors — are in scope
  // too: a NaN total cost, realisation, profit, NPV, equity, reserve or cashflow entry means the model
  // itself broke and must FAIL. Return METRICS (IRRs, yields, margins, multiples, the geared hurdle
  // reference IRR) are deliberately NOT here: they are legitimately non-computable in
  // stressed-but-plausible scenarios (a geared IRR when equity is wiped out, a margin when cost is
  // zero), and `return-bounds` above reports every one of them as REVIEW ("not computable") rather than
  // letting them read PASS.
  const returnMetricKeys = new Set<string>([
    "developer.irr",
    "developer.gearedDevelopmentIrr",
    "developer.gearedWholeOfLifeIrr",
    "developer.ungearedEarlyExitIrr",
    "developer.gearedEarlyExitIrr",
    "developer.marginOnCost",
    "developer.yieldOnCost",
    "developer.yieldOnCash",
    "developer.moneyMultiple",
    "developer.gearedHurdle",
    "kpis.developerIrr",
    "kpis.developerUngearedEarlyExitIrr",
    "kpis.developerMarginOnCost",
    "kpis.developerYieldOnCost",
    "kpis.developerGearedIrr",
    "kpis.developerGearedEarlyExitIrr",
    "kpis.developerGearedWholeOfLifeIrr",
    "kpis.developerPrimaryGearedIrr",
    "kpis.developerPrimaryIrr",
    "kpis.developerYieldOnCash",
    "kpis.developerMoneyMultiple",
    "kpis.reddamIrr",
  ]);
  for (const [prefix, obj] of [
    ["developer", result.developer],
    ["kpis", result.kpis],
  ] as const) {
    Object.entries(obj).forEach(([k, v]) => {
      const key = `${prefix}.${k}`;
      if (
        typeof v === "number" &&
        !Number.isFinite(v) &&
        !returnMetricKeys.has(key)
      )
        naNs.push(key);
      if (Array.isArray(v)) {
        const i = v.findIndex(
          (x) => typeof x === "number" && !Number.isFinite(x),
        );
        if (i >= 0) naNs.push(`${key}[${i}]`);
      }
    });
  }
  checks.push({
    id: "finite",
    area: "Cashflow model",
    level: naNs.length ? "fail" : "pass",
    label:
      "No NaN or infinite values in cashflow, cost, realisation or KPI outputs",
    detail: naNs.length ? naNs.join(", ") : "All numeric outputs are finite.",
  });

  const breaks: string[] = [];
  result.years.forEach((y, i) => {
    const prior =
      i === 0
        ? -result.campus.totalReddamCapex
        : result.years[i - 1].cumulativeCashflow;
    if (!closeTo(prior + y.reddamOperatingCashflow, y.cumulativeCashflow))
      breaks.push(`Y${y.year}`);
  });
  checks.push({
    id: "rollforward",
    area: "Cashflow model",
    level: breaks.length ? "fail" : "pass",
    label: "Cumulative cashflow roll-forward ties",
    detail: breaks.length
      ? `Breaks in ${breaks.join(", ")}.`
      : "Each year = prior cumulative + after-tax operating cash (fit-out amortisation added back).",
  });

  const rowBreaks = result.years.filter(
    (y) =>
      !closeTo(y.tuitionRevenue + y.ancillaryRevenue, y.totalRevenue) ||
      !closeTo(y.rentNet + y.outgoings, y.rentGross) ||
      !closeTo(
        y.totalRevenue -
          y.rentGross -
          y.amenityRent -
          y.variableCosts -
          y.fixedOpex,
        y.reddamEbitda,
      ),
  );
  checks.push({
    id: "row-recalc",
    area: "Cashflow model",
    level: rowBreaks.length ? "fail" : "pass",
    label: "Annual rows recalculate (revenue / rent / EBITDA)",
    detail: rowBreaks.length
      ? `Breaks in Y${rowBreaks.map((y) => y.year).join(", Y")}.`
      : "Formulas tie for every year.",
  });

  const rentPct = result.years[0]?.rentPctRevenue ?? Number.NaN;
  // Per user direction: surface as warning (never hard fail) — some Reddam scenarios may
  // legitimately run above the hard ceiling; the check informs, doesn't block.
  let rentLevel: "pass" | "warn" | "fail";
  if (rentPct <= 0.35) rentLevel = "pass";
  else rentLevel = "warn";
  const rentDetail = !Number.isFinite(rentPct)
    ? "Rent % not computable (no model years)."
    : rentPct > OPERATOR_AFFORDABILITY_CEILING
      ? `Year 1 rent = ${(rentPct * 100).toFixed(1)}% of revenue. Above ${(OPERATOR_AFFORDABILITY_CEILING * 100).toFixed(0)}% industry ceiling — review fees, enrolment, rent rate, cap rate. May still be acceptable depending on deal context.`
      : rentPct > 0.35
        ? `Year 1 rent = ${(rentPct * 100).toFixed(1)}% of revenue. Above 35% target band; verify assumptions.`
        : `Year 1 rent = ${(rentPct * 100).toFixed(1)}% of revenue. Within ≤35% target band.`;
  checks.push({
    id: "rent-pct-revenue",
    area: "Operator",
    level: rentLevel,
    label: "Rent as % of operator revenue (Y1)",
    detail: rentDetail,
  });

  const stageMix =
    a.earlyLearningShare + a.primaryShare + a.secondaryShare + a.seniorShare;
  // loop#024/#026: negative AND above-100% shares must surface here too — with
  // compensating shares the total can sit at exactly 100% while one stage is
  // silently distorted by normalisation (the compute-time flags warn; this is the
  // checks-panel mirror).
  const anomalousStage = [
    a.earlyLearningShare,
    a.primaryShare,
    a.secondaryShare,
    a.seniorShare,
  ].find((share) => !Number.isFinite(share) || share < 0 || share > 1);
  checks.push({
    id: "mix",
    area: "Inputs",
    level:
      anomalousStage !== undefined || Math.abs(stageMix - 1) > 0.02
        ? "warn"
        : "pass",
    label: "Tuition-stage mix totals ~100%",
    detail: (() => {
      if (anomalousStage === undefined) {
        return `Stage shares total ${(stageMix * 100).toFixed(1)}%. Shares auto-normalise if not exact.`;
      }
      if (!Number.isFinite(anomalousStage)) {
        return "A stage share is not a finite number — it contributes 0% after normalisation.";
      }
      if (anomalousStage < 0) {
        return `A stage share is negative (${(anomalousStage * 100).toFixed(1)}%) — it contributes 0% after normalisation and the remaining stages are scaled up.`;
      }
      return `A stage share is above 100% (${(anomalousStage * 100).toFixed(1)}%) — normalisation rescales every stage around it, distorting the whole mix.`;
    })(),
  });

  const parkAlloc =
    a.weekdayParentParkingBays +
    a.weekendSportsParkingBays +
    a.staffReservedParkingBays;
  checks.push({
    id: "parking",
    area: "Inputs",
    level: parkAlloc <= a.parkingBays ? "pass" : "warn",
    label: "Parking allocation within physical bays",
    detail: `${parkAlloc.toLocaleString("en-AU")} allocated vs ${a.parkingBays.toLocaleString("en-AU")} physical bays.`,
  });

  // Debt facility coherence — only run when facility fields are present
  if (d.facilityLimitDev != null && Number.isFinite(d.facilityLimitDev)) {
    const facilityCoversLvr =
      d.grossFacilityLimitDev <= d.totalCost * (a.constructionLvrRate + 0.005); // +0.5% tolerance
    checks.push({
      id: "facility-lvr",
      area: "Debt",
      level: facilityCoversLvr ? "pass" : "warn",
      label: `Construction facility ≤ ${Math.round(a.constructionLvrRate * 100)}% LVR`,
      detail: `Gross capacity ${compactCurrency(d.grossFacilityLimitDev)} vs TDC ${compactCurrency(d.totalCost)} × ${Math.round(a.constructionLvrRate * 100)}%; actual debt after contribution ${compactCurrency(d.facilityLimitDev)}.`,
    });

    const equityPositive =
      Number.isFinite(d.equityRequired) && d.equityRequired > 0;
    checks.push({
      id: "equity-positive",
      area: "Debt",
      level: equityPositive ? "pass" : "warn",
      label: "Equity required is positive (TDC > facility limit)",
      detail: equityPositive
        ? `Equity required: ${compactCurrency(d.equityRequired)}.`
        : "Facility limit equals or exceeds TDC — no equity injection modelled.",
    });

    // Term loan sufficiency: can it cover the construction facility + capitalized interest?
    const facilityBalanceAtPc = d.facilityLimitDev + d.debtCostDev;
    const termLoanSufficient = d.termLoanLimit >= facilityBalanceAtPc;
    checks.push({
      id: "term-loan-sufficiency",
      area: "Debt",
      level: termLoanSufficient ? "pass" : "warn",
      label: "Term loan covers construction facility + debt costs",
      detail: termLoanSufficient
        ? `Term loan ${compactCurrency(d.termLoanLimit)} ≥ facility balance ${compactCurrency(facilityBalanceAtPc)}.`
        : `Term loan ${compactCurrency(d.termLoanLimit)} < facility balance ${compactCurrency(facilityBalanceAtPc)} — equity top-up required at PC.`,
    });

    // DSCR (P6-R4 / #9) — STRUCTURAL / financing-geometry ratio. The term loan is sized on value
    // (= NOI ÷ cap rate), so DSCR = NOI ÷ (termLVR × value × debt rate) = capRate ÷ (termLVR × debt rate):
    // NOI cancels, so it is INVARIANT to rent. Covenant stress is a cap-rate / interest-rate event, not a
    // rent event — re-labelled to clarify it is not a rent-sensitive coverage test.
    checks.push({
      id: "dscr",
      area: "Debt",
      level:
        ({ na: "pass", good: "pass", warn: "warn", bad: "fail" } as const)[
          d.dscrStatus
        ] ?? "fail",
      label:
        "Debt service coverage ratio (DSCR — structural / financing geometry)",
      detail:
        d.dscrStatus === "na"
          ? "No term loan — DSCR not applicable."
          : `NOI ${compactCurrency(d.noi)} / term service ${compactCurrency(d.termLoanAnnualService)} = ${d.dscr.toFixed(2)}× (target ≥ 1.25×). Structural: = cap rate ÷ (term LVR × debt rate), INVARIANT to rent at a fixed cap rate — covenant stress is cap-rate / interest-rate driven, not rent.`,
    });
  }

  return checks;
}

// ---------- benchmarks ----------
/**
 * Data-driven construction benchmark — reads the all-in whole-building $/m² band for a
 * school-spec tier from data/education_construction_costs_2021_2026.json (HIGH-reliability:
 * QIS BGA 2025 / ACT BGA 2025 / Rawlinsons / ABS PPI 2026). NOTE the basis: these are
 * **all-in** construction rates (incl. preliminaries + builder margin), so compare them against
 * the model's all-in construction $/m² (hardConstruction + preliminaries + builderMargin) ÷ GFA,
 * NOT the net-trade `constructionPerSqmGfa` input.
 */
export function constructionBenchmark(
  tier: SchoolSpecTier = "private_mid",
): Benchmark {
  const rows = eduCosts.k12_hard_costs.whole_building_ranges_sydney as Array<{
    key?: string;
    type: string;
    low_aud_m2: number;
    mid_aud_m2: number;
    high_aud_m2: number;
    source: string;
  }>;
  const row =
    rows.find((r) => r.key === tier) ??
    rows.find((r) => r.key === "private_mid") ??
    rows[0];
  return {
    metric: "Construction / sqm GFA (all-in)",
    unit: "$/sqm",
    low: row.low_aud_m2,
    mid: row.mid_aud_m2,
    high: row.high_aud_m2,
    source: `${row.type} — ${row.source} (education_construction_costs_2021_2026.json)`,
  };
}

export const BENCHMARKS: Benchmark[] = [
  {
    metric: "Net rent / sqm (academic)",
    unit: "$/sqm",
    low: 580,
    mid: 720,
    high: 880,
    source: "CBD-fringe school leases, 2022–2025",
  },
  {
    metric: "Outgoings / sqm",
    unit: "$/sqm",
    low: 95,
    mid: 130,
    high: 165,
    source: "NSW education leases",
  },
  constructionBenchmark("private_mid"),
  {
    metric: "Cap rate (school assets)",
    unit: "%",
    low: 4.5,
    mid: 5.5,
    high: 6.5,
    source: "Knight Frank specialist desks",
  },
  {
    metric: "Tuition fee — primary",
    unit: "$/student",
    low: 28000,
    mid: 34000,
    high: 42000,
    source: "AIS NSW fees 2025",
  },
  {
    metric: "Tuition fee — secondary",
    unit: "$/student",
    low: 34000,
    mid: 42000,
    high: 52000,
    source: "AIS NSW fees 2025",
  },
  {
    metric: "Steady-state utilisation",
    unit: "%",
    low: 80,
    mid: 90,
    high: 96,
    source: "Operator benchmark range",
  },
  {
    metric: "Ramp to steady state",
    unit: "yrs",
    low: 3,
    mid: 5,
    high: 7,
    source: "New-build school openings",
  },
  {
    metric: "Rent / revenue (Y1)",
    unit: "%",
    low: 25,
    mid: 35,
    high: 45,
    source: "Affordable lease threshold",
  },
  {
    metric: "Variable cost / student",
    unit: "$/student",
    low: 9000,
    mid: 11500,
    high: 14000,
    source: "Comparable operators",
  },
];

// expose API
export const Model = {
  exitLeaseYearFor,
  compute,
  computeGearedReferenceBaseline,
  computeCore: computeCoreRaw,
  validate,
  tornado,
  tornadoBars,
  monteCarlo,
  monteCarloChunked,
  modelChecks,
  solveRentForHurdle,
  breakEvenRent,
  breakEvenEnrolment,
  computeRentDecomposition,
  computeRentBuildUp,
  rentMultiplier,
  tpcSweep,
  computeLeaseVsOwn,
  baseScenario,
  defaultScenarios,
  productionScenarios,
  normalizeScenario,
  SCENARIO_LEVER_KEYS,
  BENCHMARKS,
  DRIVERS,
  constructionBenchmark,
  RENT_PCT_REVENUE_THRESHOLD,
  RENT_PCT_REVENUE_CAUTION,
  ENROLMENT_RAMP_TOLERANCE,
  effectiveRentGrowth,
  helpers: {
    sum,
    npv,
    irr,
    blendedTuitionFee,
    parkingIncome,
    rampUtil,
    normalizeShares,
  },
};
